(function () {
  const MOBILE_QUERY = "(max-width: 767px)";
  const LOCO_LIGHTBOX_VIDEO_CLASS = "pdp-loco-lightbox-video";

  function debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), delay);
    };
  }

  function getVisibleSlides(slider) {
    return Array.from(slider.querySelectorAll(".product-images__slide")).filter(
      (slide) => slide.offsetParent !== null && getComputedStyle(slide).display !== "none"
    );
  }

  function getActiveSlideIndex(slider, slides) {
    const scrollLeft = slider.scrollLeft;
    let activeIndex = 0;
    let minDistance = Infinity;

    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - scrollLeft);
      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = index;
      }
    });

    return activeIndex;
  }

  function updateDots(dots, activeIndex) {
    dots.querySelectorAll(".pdp-loco__gallery-dot").forEach((dot, index) => {
      dot.classList.toggle("is-active", index === activeIndex);
      dot.setAttribute("aria-current", index === activeIndex ? "true" : "false");
    });
  }

  let galleryVideoLightbox = null;
  let galleryVideoLightboxState = null;

  function isVideoSlide(slide) {
    return Boolean(slide?.querySelector(".product-single__media-gallery-video"));
  }

  function getVideoSlides(slider) {
    if (!slider) return [];
    return Array.from(slider.querySelectorAll(".product-images__slide")).filter((slide) => isVideoSlide(slide));
  }

  function playAllGalleryVideos(slider) {
    if (!slider || !isVideoAutoplayEnabled(slider)) return;
    getVideoSlides(slider).forEach((slide) => playSlideVideos(slide));
  }

  function pauseAllGalleryVideos(slider) {
    if (!slider) return;

    slider.querySelectorAll(".product-images__slide").forEach((slide) => {
      if (isVideoSlide(slide)) {
        pauseSlideVideos(slide);
      }
    });
  }

  function isVideoAutoplayEnabled(node) {
    const root = node?.closest?.(".pdp-loco") || node;
    if (root?.dataset?.enableVideoAutoplay != null) {
      return root.dataset.enableVideoAutoplay === "true";
    }
    return true;
  }

  function isVideoLoopEnabled(node) {
    const media = node?.closest?.(".product-single__media-gallery-video");
    if (media?.dataset?.videoLoop != null) {
      return media.dataset.videoLoop === "true";
    }
    return node?.closest?.(".pdp-loco")?.dataset?.enableVideoLooping === "true";
  }

  function applyNativeVideoLoop(video) {
    if (!video) return;
    video.loop = isVideoLoopEnabled(video);
  }

  function getYoutubeVideoId(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.searchParams.get("v")) {
        return parsed.searchParams.get("v");
      }
      const pathId = parsed.pathname.split("/").filter(Boolean).pop();
      return pathId || "";
    } catch (error) {
      return "";
    }
  }

  function buildExternalVideoEmbedSrc(src, provider, options = {}) {
    const { autoplay = true, muted = true, loop = false } = options;

    try {
      const url = new URL(src, window.location.origin);

      if (provider === "youtube") {
        url.searchParams.set("autoplay", autoplay ? "1" : "0");
        url.searchParams.set("mute", muted ? "1" : "0");
        url.searchParams.set("enablejsapi", "1");
        url.searchParams.set("playsinline", "1");
        url.searchParams.set("controls", "1");
        if (loop) {
          const videoId = getYoutubeVideoId(url.toString());
          url.searchParams.set("loop", "1");
          if (videoId) {
            url.searchParams.set("playlist", videoId);
          }
        } else {
          url.searchParams.set("loop", "0");
          url.searchParams.delete("playlist");
        }
      } else if (provider === "vimeo") {
        url.searchParams.set("autoplay", autoplay ? "1" : "0");
        url.searchParams.set("muted", muted ? "1" : "0");
        url.searchParams.set("loop", loop ? "1" : "0");
        url.searchParams.set("controls", "1");
      }

      return url.toString();
    } catch (error) {
      return src;
    }
  }

  async function playVideoMuted(video) {
    if (!video) return;

    applyNativeVideoLoop(video);
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute("muted", "");

    if (video.readyState < 1) {
      video.load();
    }

    try {
      await video.play();
    } catch (error) {
      /* Playback blocked — keep poster visible */
    }
  }

  function getGalleryNativeVideoSource(video) {
    if (!video) return "";

    const sourceEl = video.querySelector("source");
    return (
      video.currentSrc ||
      video.src ||
      sourceEl?.src ||
      video.getAttribute("src") ||
      ""
    ).trim();
  }

  function resetGalleryVideoLightboxMedia(media) {
    if (!media) return;
    media.innerHTML = "";
    media.style.removeProperty("width");
    media.style.removeProperty("height");
    media.style.removeProperty("aspect-ratio");
  }

  function ensureGalleryVideoLightbox() {
    if (galleryVideoLightbox) return galleryVideoLightbox;

    const lightbox = document.createElement("div");
    lightbox.className = "pdp-loco__gallery-video-lightbox";
    lightbox.hidden = true;
    lightbox.setAttribute("aria-hidden", "true");
    lightbox.innerHTML = `
      <div class="pdp-loco__gallery-video-lightbox__backdrop" data-pdp-gallery-video-close></div>
      <div class="pdp-loco__gallery-video-lightbox__dialog" role="dialog" aria-modal="true" aria-label="Product video">
        <button type="button" class="pdp-loco__gallery-video-lightbox__close" data-pdp-gallery-video-close aria-label="Close">×</button>
        <div class="pdp-loco__gallery-video-lightbox__media"></div>
      </div>
    `;

    document.body.appendChild(lightbox);

    lightbox.querySelectorAll("[data-pdp-gallery-video-close]").forEach((node) => {
      node.addEventListener("click", closeGalleryVideoLightbox);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && galleryVideoLightbox && galleryVideoLightbox.classList.contains("is-open")) {
        closeGalleryVideoLightbox();
      }
    });

    galleryVideoLightbox = lightbox;
    return lightbox;
  }

  function closeGalleryVideoLightbox() {
    const lightbox = galleryVideoLightbox;
    if (!lightbox || !lightbox.classList.contains("is-open")) return;

    const resumeSlider = galleryVideoLightboxState?.slider || galleryVideoLightboxState?.slide?.closest(".product-images");

    if (galleryVideoLightboxState?.type === "native") {
      galleryVideoLightboxState.video?.pause();
    }

    if (galleryVideoLightboxState?.type === "external") {
      galleryVideoLightboxState.iframe?.removeAttribute("src");
    }

    resetGalleryVideoLightboxMedia(lightbox.querySelector(".pdp-loco__gallery-video-lightbox__media"));

    lightbox.hidden = true;
    lightbox.classList.remove("is-open");
    lightbox.setAttribute("aria-hidden", "true");
    document.body.classList.remove("pdp-loco-gallery-video-lightbox-open");
    galleryVideoLightboxState = null;

    if (resumeSlider && isVideoAutoplayEnabled(resumeSlider)) {
      playAllGalleryVideos(resumeSlider);
    }
  }

  function openGalleryVideoLightbox(media) {
    if (!media) return;

    const slide = media.closest(".product-images__slide");
    const slider = slide?.closest(".product-images");
    const nativeVideo = media.querySelector("video.pdp-gallery-video");
    const iframe = media.querySelector("iframe");
    const provider = media.dataset.provider || "";
    const lightbox = ensureGalleryVideoLightbox();
    const lightboxMedia = lightbox.querySelector(".pdp-loco__gallery-video-lightbox__media");

    if (!lightboxMedia) return;

    closeGalleryVideoLightbox();
    pauseAllGalleryVideos(slider);
    resetGalleryVideoLightboxMedia(lightboxMedia);

    if (nativeVideo) {
      const source = getGalleryNativeVideoSource(nativeVideo);
      if (!source) return;

      const popupVideo = document.createElement("video");
      popupVideo.className = `${LOCO_LIGHTBOX_VIDEO_CLASS} pdp-gallery-video`;
      popupVideo.src = source;
      popupVideo.controls = true;
      popupVideo.autoplay = true;
      popupVideo.muted = false;
      popupVideo.playsInline = true;
      popupVideo.loop = isVideoLoopEnabled(nativeVideo);
      lightboxMedia.appendChild(popupVideo);
      bindGalleryVideoFullscreenEvents(popupVideo);
      fitLightboxVideo(lightboxMedia, popupVideo);

      galleryVideoLightboxState = { type: "native", video: popupVideo, slide, slider };
      const playPromise = popupVideo.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          popupVideo.muted = true;
          popupVideo.play().catch(() => {});
        });
      }
    } else if (iframe) {
      const storedSrc = storeIframeEmbedSrc(iframe);
      if (!storedSrc) return;

      const embedUrl =
        parseVideoEmbedUrl(storedSrc, { autoplay: true, muted: false }) ||
        buildExternalVideoEmbedSrc(storedSrc, provider, {
          autoplay: true,
          muted: false,
          loop: isVideoLoopEnabled(media),
        });

      const popupIframe = document.createElement("iframe");
      popupIframe.src = embedUrl;
      popupIframe.allow = "autoplay; encrypted-media; picture-in-picture; fullscreen";
      popupIframe.allowFullscreen = true;
      popupIframe.title = "Product video";
      lightboxMedia.appendChild(popupIframe);
      fitLightboxIframe(lightboxMedia, 16 / 9);

      galleryVideoLightboxState = { type: "external", iframe: popupIframe, slide, slider };
    } else {
      return;
    }

    lightbox.hidden = false;
    lightbox.classList.add("is-open");
    lightbox.setAttribute("aria-hidden", "false");
    document.body.classList.add("pdp-loco-gallery-video-lightbox-open");
    lightbox.querySelector(".pdp-loco__gallery-video-lightbox__close")?.focus();
  }

  function syncGalleryVideoControlState(media) {
    if (!media) return;

    const video = media.querySelector("video.pdp-gallery-video");
    const isPaused = video ? video.paused : media.classList.contains("is-paused");

    media.classList.toggle("is-paused", isPaused);
    updateGalleryVideoNativeControls(media);
  }

  function updateGalleryVideoNativeControls(media) {
    if (!media) return;

    const video = media.querySelector("video.pdp-gallery-video");
    const showControls =
      media.classList.contains("is-video-hovered") ||
      media.classList.contains("is-paused") ||
      media.matches(":focus-within");

    media.classList.toggle("is-video-interactive", showControls);

    if (video) {
      video.controls = showControls;
    }
  }

  function setGalleryVideoHovered(media, hovered) {
    if (!media) return;
    media.classList.toggle("is-video-hovered", hovered);
    updateGalleryVideoNativeControls(media);
  }

  function isLightboxVideoFullscreen(video) {
    if (!video) return false;

    const fullscreenElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (fullscreenElement === video) return true;
    if (fullscreenElement && video.contains(fullscreenElement)) return true;
    if (video.webkitDisplayingFullscreen) return true;

    return false;
  }

  function isGalleryVideoFullscreen(video) {
    return isLightboxVideoFullscreen(video);
  }

  function syncGalleryVideoFullscreenStyles() {
    document.querySelectorAll(`video.pdp-gallery-video, video.${LOCO_LIGHTBOX_VIDEO_CLASS}`).forEach((video) => {
      const isFullscreen = isLightboxVideoFullscreen(video);
      const media = video.closest(".product-single__media-gallery-video");

      video.classList.toggle("is-native-fullscreen", isFullscreen);
      media?.classList.toggle("is-video-fullscreen", isFullscreen);
    });
  }

  function ensureGalleryVideoFullscreenListener() {
    if (document.documentElement.dataset.pdpGalleryFsBound === "true") return;
    document.documentElement.dataset.pdpGalleryFsBound = "true";

    const handler = () => syncGalleryVideoFullscreenStyles();
    document.addEventListener("fullscreenchange", handler);
    document.addEventListener("webkitfullscreenchange", handler);
  }

  function bindGalleryVideoFullscreenEvents(video) {
    if (!video || video.dataset.pdpGalleryFsBound === "true") return;
    video.dataset.pdpGalleryFsBound = "true";

    const sync = () => syncGalleryVideoFullscreenStyles();

    video.addEventListener("webkitbeginfullscreen", sync);
    video.addEventListener("webkitendfullscreen", sync);
    video.addEventListener("enterpictureinpicture", sync);
    video.addEventListener("leavepictureinpicture", sync);
  }

  function setExternalGalleryVideoMuted(media, muted) {
    const iframe = media?.querySelector("iframe");
    const provider = media?.dataset?.provider;
    if (!iframe || !provider) return;

    media.dataset.galleryMuted = muted ? "true" : "false";

    if (provider === "youtube") {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: muted ? "mute" : "unMute", args: [] }),
        "*"
      );
      return;
    }

    if (provider === "vimeo") {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ method: "setVolume", value: muted ? 0 : 1 }),
        "*"
      );
    }
  }

  function pauseGalleryMedia(media) {
    if (!media) return;

    const video = media.querySelector("video.pdp-gallery-video");

    if (video) {
      video.pause();
    } else {
      pauseExternalGalleryMedia(media);
    }

    syncGalleryVideoControlState(media);
  }

  function playGalleryMedia(media) {
    if (!media) return;

    const video = media.querySelector("video.pdp-gallery-video");

    if (video) {
      const playPromise = video.muted ? playVideoMuted(video) : video.play();

      Promise.resolve(playPromise).then(() => {
        if (!video.paused) {
          hideVideoPoster(media);
        }
        syncGalleryVideoControlState(media);
      });
      return;
    }

    media.classList.remove("is-paused");
    playExternalGalleryMedia(media);
    syncGalleryVideoControlState(media);
  }

  function toggleGalleryVideoPlayback(media, event) {
    event.preventDefault();
    event.stopPropagation();

    const video = media.querySelector("video.pdp-gallery-video");
    const isPaused = video ? video.paused : media.classList.contains("is-paused");

    if (isPaused) {
      playGalleryMedia(media);
      return;
    }

    pauseGalleryMedia(media);
  }

  function bindGalleryVideoControls(media) {
    if (!media || media.dataset.pdpGalleryVideoControlsBound === "true") return;
    media.dataset.pdpGalleryVideoControlsBound = "true";
    media.dataset.galleryMuted = "true";

    const video = media.querySelector("video.pdp-gallery-video");
    if (video) {
      video.controls = false;
    }

    media.addEventListener("mouseenter", () => setGalleryVideoHovered(media, true));
    media.addEventListener("mouseleave", () => setGalleryVideoHovered(media, false));
    media.addEventListener("focusin", () => setGalleryVideoHovered(media, true));
    media.addEventListener("focusout", (event) => {
      if (!media.contains(event.relatedTarget)) {
        setGalleryVideoHovered(media, false);
      }
    });

    media.addEventListener("click", (event) => {
      if (media.classList.contains("is-video-interactive")) return;
      if (event.target.closest("video, iframe")) return;
      event.preventDefault();
      openGalleryVideoLightbox(media);
    });

    video?.addEventListener("play", () => {
      hideVideoPoster(media);
      syncGalleryVideoControlState(media);
    });
    video?.addEventListener("pause", () => syncGalleryVideoControlState(media));
    video?.addEventListener("volumechange", () => syncGalleryVideoControlState(media));

    if (video) {
      bindGalleryVideoFullscreenEvents(video);
    }

    syncGalleryVideoControlState(media);
  }

  function playSlideVideos(slide) {
    if (!slide) return;

    slide.querySelectorAll("video.pdp-gallery-video, .product-single__media-gallery-video video").forEach((video) => {
      const media = video.closest(".product-single__media-gallery-video");
      playVideoMuted(video).then(() => {
        if (!video.paused) {
          hideVideoPoster(media);
        }
        syncGalleryVideoControlState(media);
      });
    });

    playExternalSlideVideo(slide);
    slide.querySelectorAll(".product-single__media-gallery-video").forEach((media) => {
      media.classList.remove("is-paused");
      syncGalleryVideoControlState(media);
    });
  }

  function hideVideoPoster(media) {
    if (!media) return;
    media.classList.add("is-playing");
    media.querySelector(".pdp-gallery-video-poster")?.classList.add("is-hidden");
  }

  function showVideoPoster(media) {
    if (!media) return;
    media.classList.remove("is-playing");
    media.querySelector(".pdp-gallery-video-poster")?.classList.remove("is-hidden");
  }

  function getIframeEmbedSrc(iframe) {
    return (
      iframe.dataset.pdpGallerySrc ||
      iframe.getAttribute("data-src") ||
      iframe.src ||
      ""
    ).trim();
  }

  function storeIframeEmbedSrc(iframe) {
    const src = getIframeEmbedSrc(iframe);
    if (!src || src === "about:blank") return "";
    if (!iframe.dataset.pdpGallerySrc) {
      iframe.dataset.pdpGallerySrc = src;
    }
    return iframe.dataset.pdpGallerySrc;
  }

  function pauseExternalGalleryMedia(media) {
    const iframe = media?.querySelector("iframe");
    const provider = media?.dataset?.provider;
    if (!iframe || !provider) return;

    if (provider === "youtube") {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
        "*"
      );
      return;
    }

    if (provider === "vimeo") {
      iframe.contentWindow?.postMessage(JSON.stringify({ method: "pause" }), "*");
    }

    media.classList.add("is-paused");
  }

  function playExternalGalleryMedia(media) {
    const iframe = media?.querySelector("iframe");
    const provider = media?.dataset?.provider;
    if (!iframe || !provider) return;

    media.classList.remove("is-paused");

    if (provider === "youtube") {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "playVideo", args: [] }),
        "*"
      );
    } else if (provider === "vimeo") {
      iframe.contentWindow?.postMessage(JSON.stringify({ method: "play" }), "*");
    }

    setExternalGalleryVideoMuted(media, media.dataset.galleryMuted !== "false");
    hideVideoPoster(media);
    syncGalleryVideoControlState(media);
  }

  function pauseExternalSlideVideo(slide) {
    const iframe = slide?.querySelector(".product-single__media-gallery-video iframe");
    if (!iframe) return;

    const provider = slide.querySelector(".product-single__media-gallery-video[data-provider]")?.dataset?.provider;

    if (provider === "youtube") {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
        "*"
      );
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "mute", args: [] }),
        "*"
      );
      return;
    }

    if (provider === "vimeo") {
      iframe.contentWindow?.postMessage(JSON.stringify({ method: "pause" }), "*");
      iframe.contentWindow?.postMessage(JSON.stringify({ method: "setVolume", value: 0 }), "*");
    }
  }

  function playExternalSlideVideo(slide) {
    const container = slide?.querySelector(".product-single__media-gallery-video[data-provider]");
    const iframe = container?.querySelector("iframe");
    if (!iframe) return;

    const provider = container.dataset.provider;
    const storedSrc = storeIframeEmbedSrc(iframe);
    if (!storedSrc) return;

    const autoplayEnabled = isVideoAutoplayEnabled(container);
    const autoplaySrc = buildExternalVideoEmbedSrc(storedSrc, provider, {
      autoplay: autoplayEnabled,
      muted: true,
      loop: isVideoLoopEnabled(container),
    });
    const currentSrc = (iframe.src || "").trim();
    const onIframeReady = () => {
      hideVideoPoster(container);
      container.classList.remove("is-paused");
      syncGalleryVideoControlState(container);
    };

    if (!currentSrc || currentSrc === "about:blank" || currentSrc !== autoplaySrc) {
      iframe.addEventListener("load", onIframeReady, { once: true });
      iframe.src = autoplaySrc;
      return;
    }

    onIframeReady();

    if (provider === "youtube") {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "playVideo", args: [] }),
        "*"
      );
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "mute", args: [] }),
        "*"
      );
      return;
    }

    if (provider === "vimeo") {
      iframe.contentWindow?.postMessage(JSON.stringify({ method: "play" }), "*");
      iframe.contentWindow?.postMessage(JSON.stringify({ method: "setVolume", value: 0 }), "*");
    }
  }

  function pauseSlideVideos(slide) {
    if (!slide) return;

    slide.querySelectorAll("video").forEach((video) => {
      video.pause();
      video.muted = true;
    });

    showVideoPoster(slide.querySelector(".product-single__media-gallery-video"));
    pauseExternalSlideVideo(slide);
    slide.querySelectorAll(".product-single__media-gallery-video").forEach((media) => {
      media.classList.add("is-paused");
      media.dataset.galleryMuted = "true";
      syncGalleryVideoControlState(media);
    });
  }

  function syncGalleryVideosForSlider(slider) {
    if (!slider) return;
    playAllGalleryVideos(slider);
  }

  function initGalleryVideos(scope) {
    const slider = scope.querySelector(".product-images");
    if (!slider || slider.dataset.pdpGalleryVideosBound === "true") return;

    const slides = Array.from(slider.querySelectorAll(".product-images__slide")).filter((slide) =>
      slide.querySelector(".product-single__media-gallery-video")
    );

    if (!slides.length) {
      slider.dataset.pdpGalleryVideosBound = "true";
      return;
    }

    ensureGalleryVideoFullscreenListener();

    slides.forEach((slide) => {
      slide.querySelectorAll(".product-single__media-gallery-video").forEach((media) => {
        bindGalleryVideoControls(media);
      });

      slide.querySelectorAll("video.pdp-gallery-video, .product-single__media-gallery-video video").forEach((video) => {
        if (video.dataset.pdpGalleryVideoBound === "true") return;
        video.dataset.pdpGalleryVideoBound = "true";
        applyNativeVideoLoop(video);
        video.addEventListener("playing", () => {
          hideVideoPoster(video.closest(".product-single__media-gallery-video"));
          syncGalleryVideoControlState(video.closest(".product-single__media-gallery-video"));
        });
        video.addEventListener("ended", () => {
          if (!isVideoLoopEnabled(video)) {
            showVideoPoster(video.closest(".product-single__media-gallery-video"));
          }
        });
      });
    });

    const isScrollableGallery = () => slider.scrollWidth > slider.clientWidth + 1;
    const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

    const onGalleryScroll = debounce(() => {
      syncGalleryVideosForSlider(slider);
    }, 80);

    if (isMobile() || isScrollableGallery()) {
      playAllGalleryVideos(slider);
      slider.addEventListener("scroll", onGalleryScroll, { passive: true });
      slider._pdpGalleryVideoOnScroll = onGalleryScroll;
    } else if ("IntersectionObserver" in window) {
      playAllGalleryVideos(slider);

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
              if (isVideoAutoplayEnabled(entry.target)) {
                playSlideVideos(entry.target);
              }
            } else {
              pauseSlideVideos(entry.target);
            }
          });
        },
        {
          threshold: [0, 0.35, 0.5, 1],
        }
      );

      slides.forEach((slide) => observer.observe(slide));
      slider._pdpGalleryVideoObserver = observer;
    } else {
      playAllGalleryVideos(slider);
    }

    slider.dataset.pdpGalleryVideosBound = "true";
  }

  function initMobileGallery(scope) {
    if (!window.matchMedia(MOBILE_QUERY).matches) return;

    const pdpRoot = scope.classList?.contains("pdp-loco") ? scope : scope.querySelector(".pdp-loco");
    const container = (pdpRoot || scope).querySelector(".product-image-container");
    const slider = (pdpRoot || scope).querySelector(".product-images");
    const dotsWrap = (pdpRoot || scope).querySelector("[data-gallery-dots]");

    if (!container || !slider || !dotsWrap || container.dataset.pdpGalleryBound === "true") {
      return;
    }

    const useThemePagination = (pdpRoot || scope).dataset?.displayPagination === "true";

    if (useThemePagination) {
      dotsWrap.hidden = true;
      container.dataset.pdpGalleryBound = "true";
      return;
    }

    const slides = getVisibleSlides(slider);
    if (slides.length <= 1) {
      dotsWrap.hidden = true;
      container.dataset.pdpGalleryBound = "true";
      return;
    }

    dotsWrap.hidden = false;
    dotsWrap.innerHTML = "";

    slides.forEach((slide, index) => {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = "pdp-loco__gallery-dot" + (index === 0 ? " is-active" : "");
      dot.setAttribute("aria-label", `Imagen ${index + 1} de ${slides.length}`);
      dot.setAttribute("aria-current", index === 0 ? "true" : "false");
      dot.addEventListener("click", () => {
        slider.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
        window.setTimeout(() => syncGalleryVideosForSlider(slider), 120);
      });
      dotsWrap.appendChild(dot);
    });

    const onScroll = debounce(() => {
      updateDots(dotsWrap, getActiveSlideIndex(slider, slides));
      syncGalleryVideosForSlider(slider);
    }, 80);

    slider.addEventListener("scroll", onScroll, { passive: true });
    container.dataset.pdpGalleryBound = "true";
  }

  function initCompleteLook(root) {
    const closeAllCompleteLookBars = (exceptCard) => {
      root.querySelectorAll(".pdp-loco__complete-card.is-quick-add-open").forEach((card) => {
        if (card === exceptCard) return;
        card.classList.remove("is-quick-add-open");
        card.querySelector("[data-pdp-complete-toggle]")?.setAttribute("aria-expanded", "false");
      });
    };

    root.querySelectorAll(".pdp-loco__complete-track").forEach((track) => {
      if (track.dataset.pdpBound === "true") return;
      track.dataset.pdpBound = "true";

      track.querySelectorAll("[data-pdp-complete-toggle]").forEach((toggle) => {
        toggle.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();

          const card = toggle.closest(".pdp-loco__complete-card");
          if (!card) return;

          const willOpen = !card.classList.contains("is-quick-add-open");
          closeAllCompleteLookBars(card);

          if (willOpen) {
            card.classList.add("is-quick-add-open");
            toggle.setAttribute("aria-expanded", "true");
          } else {
            card.classList.remove("is-quick-add-open");
            toggle.setAttribute("aria-expanded", "false");
          }
        });
      });

      track.querySelectorAll("product-tab-card-hover-bar").forEach((bar) => {
        bar.addEventListener("click", (event) => event.stopPropagation());
      });
    });
  }

  function ensureCompleteLookDocumentClose() {
    if (document.documentElement.dataset.pdpCompleteLookDocBound === "true") return;
    document.documentElement.dataset.pdpCompleteLookDocBound = "true";

    document.addEventListener("click", (event) => {
      if (event.target.closest(".pdp-loco__complete-card")) return;

      document.querySelectorAll(".pdp-loco__complete-card.is-quick-add-open").forEach((card) => {
        card.classList.remove("is-quick-add-open");
        card.querySelector("[data-pdp-complete-toggle]")?.setAttribute("aria-expanded", "false");
      });
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;

      document.querySelectorAll(".pdp-loco__complete-card.is-quick-add-open").forEach((card) => {
        card.classList.remove("is-quick-add-open");
        card.querySelector("[data-pdp-complete-toggle]")?.setAttribute("aria-expanded", "false");
      });
    });
  }

  const SATISFIED_CONTROL_PAUSE_LABEL = "Pausar video";
  const SATISFIED_CONTROL_PLAY_LABEL = "Reproducir video";
  const SATISFIED_CONTROL_MUTE_LABEL = "Silenciar video";
  const SATISFIED_CONTROL_UNMUTE_LABEL = "Activar sonido";

  function isSatisfiedInteractiveTarget(target) {
    return Boolean(target?.closest?.("[data-pdp-satisfied-toggle], [data-pdp-satisfied-mute]"));
  }

  let satisfiedLightbox = null;
  let satisfiedLightboxState = null;

  function isDirectVideoUrl(url) {
    if (!url) return false;

    try {
      const parsed = new URL(url);
      const path = parsed.pathname.toLowerCase();
      const host = parsed.hostname.toLowerCase();

      if (/\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(path)) return true;
      if (host.includes("cdninstagram.com") || host.includes("fbcdn.net")) return true;
      if (path.includes("/video/") || path.includes("/v/t")) return true;
    } catch (error) {
      return false;
    }

    return false;
  }

  function parseVideoEmbedUrl(url, options = {}) {
    const { autoplay = true, muted = false } = options;
    if (!url) return null;

    try {
      const parsed = new URL(url);

      if (parsed.hostname.includes("youtu")) {
        let videoId = parsed.searchParams.get("v");

        if (!videoId && parsed.pathname.includes("/shorts/")) {
          videoId = parsed.pathname.split("/shorts/")[1]?.split("/")[0];
        }

        if (!videoId) {
          const parts = parsed.pathname.split("/").filter(Boolean);
          videoId = parts[parts.length - 1];
        }

        if (!videoId) return null;

        const params = new URLSearchParams({
          playsinline: "1",
          controls: "1",
          rel: "0",
        });
        if (autoplay) params.set("autoplay", "1");
        if (muted) params.set("mute", "1");

        return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
      }

      if (parsed.hostname.includes("vimeo")) {
        const parts = parsed.pathname.split("/").filter(Boolean);
        const videoId = parts[parts.length - 1];
        if (!videoId) return null;

        const params = new URLSearchParams();
        if (autoplay) params.set("autoplay", "1");
        if (muted) params.set("muted", "1");

        const query = params.toString();
        return `https://player.vimeo.com/video/${videoId}${query ? `?${query}` : ""}`;
      }
    } catch (error) {
      return null;
    }

    return null;
  }

  function ensureSatisfiedLightbox() {
    if (satisfiedLightbox) return satisfiedLightbox;

    const lightbox = document.createElement("div");
    lightbox.className = "pdp-loco__satisfied-lightbox";
    lightbox.hidden = true;
    lightbox.setAttribute("aria-hidden", "true");
    lightbox.innerHTML = `
      <div class="pdp-loco__satisfied-lightbox__backdrop" data-pdp-satisfied-close></div>
      <div class="pdp-loco__satisfied-lightbox__dialog" role="dialog" aria-modal="true" aria-label="Video cliente satisfecho">
        <button type="button" class="pdp-loco__satisfied-lightbox__close" data-pdp-satisfied-close aria-label="Cerrar">×</button>
        <div class="pdp-loco__satisfied-lightbox__media"></div>
      </div>
    `;

    document.body.appendChild(lightbox);

    lightbox.querySelectorAll("[data-pdp-satisfied-close]").forEach((node) => {
      node.addEventListener("click", closeSatisfiedLightbox);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && satisfiedLightbox && !satisfiedLightbox.hidden) {
        closeSatisfiedLightbox();
      }
    });

    satisfiedLightbox = lightbox;
    return lightbox;
  }

  function updateSatisfiedCardToggle(card, isPaused) {
    const toggle = card?.querySelector("[data-pdp-satisfied-toggle]");
    if (!toggle) return;

    card.classList.toggle("is-paused", isPaused);
    toggle.setAttribute("aria-label", isPaused ? SATISFIED_CONTROL_PLAY_LABEL : SATISFIED_CONTROL_PAUSE_LABEL);
  }

  function updateSatisfiedCardMute(card, isMuted) {
    const muteToggle = card?.querySelector("[data-pdp-satisfied-mute]");
    if (!muteToggle) return;

    card.classList.toggle("is-muted", isMuted);
    muteToggle.setAttribute("aria-label", isMuted ? SATISFIED_CONTROL_UNMUTE_LABEL : SATISFIED_CONTROL_MUTE_LABEL);
  }

  function resetSatisfiedLightboxMedia(media) {
    if (!media) return;
    media.innerHTML = "";
    media.style.removeProperty("width");
    media.style.removeProperty("height");
    media.style.removeProperty("aspect-ratio");
  }

  function fitLightboxVideo(media, video) {
    const fit = () => {
      const maxWidth = window.innerWidth * 0.92;
      const maxHeight = window.innerHeight * 0.92;
      const videoWidth = video.videoWidth || 9;
      const videoHeight = video.videoHeight || 16;
      const ratio = videoWidth / videoHeight;

      let width = maxWidth;
      let height = width / ratio;

      if (height > maxHeight) {
        height = maxHeight;
        width = height * ratio;
      }

      media.style.width = `${Math.round(width)}px`;
      media.style.height = `${Math.round(height)}px`;
      media.style.aspectRatio = `${videoWidth} / ${videoHeight}`;
    };

    if (video.readyState >= 1) {
      fit();
    } else {
      video.addEventListener("loadedmetadata", fit, { once: true });
    }
  }

  function fitLightboxIframe(media, aspectRatio = 16 / 9) {
    const maxWidth = window.innerWidth * 0.92;
    const maxHeight = window.innerHeight * 0.92;
    let width = Math.min(maxWidth, 840);
    let height = width / aspectRatio;

    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }

    media.style.width = `${Math.round(width)}px`;
    media.style.height = `${Math.round(height)}px`;
    media.style.removeProperty("aspect-ratio");
  }

  function fitSatisfiedLightboxVideo(media, video) {
    fitLightboxVideo(media, video);
  }

  function fitSatisfiedLightboxIframe(media) {
    fitLightboxIframe(media, 9 / 16);
  }

  function ensureLightboxVideoResizeListener() {
    if (document.documentElement.dataset.pdpLightboxResizeBound === "true") return;
    document.documentElement.dataset.pdpLightboxResizeBound = "true";

    window.addEventListener(
      "resize",
      debounce(() => {
        if (satisfiedLightboxState?.type === "file" && satisfiedLightboxState.video) {
          const media = satisfiedLightbox?.querySelector(".pdp-loco__satisfied-lightbox__media");
          if (media) fitLightboxVideo(media, satisfiedLightboxState.video);
        }

        if (galleryVideoLightboxState?.type === "native" && galleryVideoLightboxState.video) {
          const media = galleryVideoLightbox?.querySelector(".pdp-loco__gallery-video-lightbox__media");
          if (media) fitLightboxVideo(media, galleryVideoLightboxState.video);
        }
      }, 150)
    );
  }

  function pauseSatisfiedCard(card) {
    if (!card) return;

    const video = card.querySelector(".pdp-loco__satisfied-video");
    if (video) {
      video.pause();
      updateSatisfiedCardToggle(card, true);
    }
  }

  function playSatisfiedCardInline(card, { forceMuted = false } = {}) {
    if (!card) return;

    const video = card.querySelector(".pdp-loco__satisfied-video");
    const source = video?.dataset.src || video?.src || video?.getAttribute("src");
    if (!video || !source) return;

    if (!video.src) {
      video.src = source;
    }

    if (forceMuted) {
      video.muted = true;
      updateSatisfiedCardMute(card, true);
    }

    const playPromise = video.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
    updateSatisfiedCardToggle(card, false);
  }

  function toggleSatisfiedCardMute(card) {
    const video = card?.querySelector(".pdp-loco__satisfied-video");
    if (!video) return;

    video.muted = !video.muted;
    updateSatisfiedCardMute(card, video.muted);
  }

  function toggleSatisfiedCardPlayback(card) {
    const video = card?.querySelector(".pdp-loco__satisfied-video");
    if (!video) return;

    if (video.paused) {
      card.dataset.pdpUserPaused = "false";
      playSatisfiedCardInline(card);
      return;
    }

    card.dataset.pdpUserPaused = "true";
    pauseSatisfiedCard(card);
  }

  function getSatisfiedCardSource(card) {
    if (!card) return null;

    let source = null;

    if (card.dataset.videoType === "file") {
      source =
        card.dataset.videoSrc ||
        card.querySelector(".pdp-loco__satisfied-video")?.dataset.src ||
        card.querySelector(".pdp-loco__satisfied-video")?.getAttribute("src") ||
        null;
    } else if (card.dataset.videoType === "url") {
      const rawUrl = card.dataset.videoUrl;
      if (isDirectVideoUrl(rawUrl)) {
        source = rawUrl;
      } else {
        source = parseVideoEmbedUrl(rawUrl, { autoplay: true, muted: false });
      }
    }

    if (!source) return null;
    if (source.startsWith("//")) {
      source = `${window.location.protocol}${source}`;
    } else if (source.startsWith("/")) {
      source = `${window.location.origin}${source}`;
    }

    return source;
  }

  function closeSatisfiedLightbox() {
    const lightbox = satisfiedLightbox;
    if (!lightbox || !lightbox.classList.contains("is-open")) return;

    const resumeCard = satisfiedLightboxState?.card;

    if (satisfiedLightboxState?.type === "file") {
      satisfiedLightboxState.video?.pause();
    }

    if (satisfiedLightboxState?.type === "url" && satisfiedLightboxState.iframe) {
      satisfiedLightboxState.iframe.removeAttribute("src");
    }

    resetSatisfiedLightboxMedia(lightbox.querySelector(".pdp-loco__satisfied-lightbox__media"));

    lightbox.hidden = true;
    lightbox.classList.remove("is-open");
    lightbox.setAttribute("aria-hidden", "true");
    document.body.classList.remove("pdp-loco-satisfied-lightbox-open");
    satisfiedLightboxState = null;

    if (resumeCard?.classList.contains("is-file")) {
      playSatisfiedCardInline(resumeCard, { forceMuted: true });
    }
  }

  function openSatisfiedLightbox(card) {
    const source = getSatisfiedCardSource(card);
    if (!source) return;

    const lightbox = ensureSatisfiedLightbox();
    const media = lightbox.querySelector(".pdp-loco__satisfied-lightbox__media");
    if (!media) return;

    closeSatisfiedLightbox();
    pauseSatisfiedCard(card);

    resetSatisfiedLightboxMedia(media);

    const useVideoElement =
      card.dataset.videoType === "file" ||
      (card.dataset.videoType === "url" && isDirectVideoUrl(card.dataset.videoUrl));

    if (useVideoElement) {
      const video = document.createElement("video");
      video.className = LOCO_LIGHTBOX_VIDEO_CLASS;
      video.src = source;
      video.playsInline = true;
      video.loop = true;
      video.autoplay = true;
      video.controls = true;
      video.muted = false;
      media.appendChild(video);
      bindGalleryVideoFullscreenEvents(video);
      fitLightboxVideo(media, video);

      satisfiedLightboxState = { type: "file", video, card };
      const playPromise = video.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          video.muted = true;
          video.play().catch(() => {});
        });
      }
    } else {
      const iframe = document.createElement("iframe");
      iframe.src = source;
      iframe.allow = "autoplay; encrypted-media; picture-in-picture; fullscreen";
      iframe.allowFullscreen = true;
      iframe.title = "Cliente satisfecho";
      media.appendChild(iframe);
      fitSatisfiedLightboxIframe(media);

      satisfiedLightboxState = { type: "url", iframe, embedUrl: source, card };
    }

    lightbox.hidden = false;
    lightbox.classList.add("is-open");
    lightbox.setAttribute("aria-hidden", "false");
    document.body.classList.add("pdp-loco-satisfied-lightbox-open");
    lightbox.querySelector(".pdp-loco__satisfied-lightbox__close")?.focus();
  }

  function shouldSuppressSatisfiedOpen(section) {
    return section?.dataset.pdpSatisfiedSuppressClick === "true";
  }

  function bindSatisfiedCardOpen(card) {
    if (!card || card.dataset.pdpOpenBound === "true") return;
    card.dataset.pdpOpenBound = "true";

    const section = card.closest(".pdp-loco__satisfied-customers");
    const media = card.querySelector("[data-pdp-satisfied-expand]") || card;

    const handleOpen = (event) => {
      if (shouldSuppressSatisfiedOpen(section)) return;
      if (event?.target?.closest?.("[data-pdp-satisfied-toggle], [data-pdp-satisfied-mute]")) return;

      event?.preventDefault?.();
      event?.stopPropagation?.();
      openSatisfiedLightbox(card);
    };

    media.addEventListener("click", handleOpen);

    media.addEventListener("keydown", (event) => {
      if (event.target.closest("[data-pdp-satisfied-toggle], [data-pdp-satisfied-mute]")) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      handleOpen(event);
    });
  }

  const SATISFIED_AUTOPLAY_MS = 5000;

  function getSatisfiedCarouselSection(node) {
    if (!node) return null;
    if (node.classList?.contains("pdp-loco__satisfied-customers")) return node;
    if (node.classList?.contains("pdp-loco__satisfied-track")) {
      return node.closest(".pdp-loco__satisfied-customers");
    }
    return node.closest?.(".pdp-loco__satisfied-customers") || null;
  }

  function getSatisfiedCarousel(section) {
    const resolvedSection = getSatisfiedCarouselSection(section);
    const viewport = resolvedSection?.querySelector("[data-pdp-satisfied-track]");
    if (!viewport) return null;

    const slides = [...viewport.querySelectorAll(".pdp-loco__satisfied-card")].filter(
      (slide) => slide.offsetParent !== null && getComputedStyle(slide).display !== "none"
    );

    return { section: resolvedSection, viewport, slides };
  }

  function getSatisfiedSlideMetrics(viewport, slides) {
    const firstSlide = slides[0];
    const gap =
      Number.parseFloat(getComputedStyle(viewport).columnGap || getComputedStyle(viewport).gap) || 0;
    const slideWidth = firstSlide?.getBoundingClientRect().width || 0;

    return {
      gap,
      slideWidth,
      step: slideWidth > 0 ? slideWidth + gap : Math.max(viewport.clientWidth * 0.85, 200),
    };
  }

  function getSatisfiedIndexFromScroll(viewport, slides) {
    const { step } = getSatisfiedSlideMetrics(viewport, slides);
    if (!step || !slides.length) return 0;

    const maxIndex = slides.length - 1;
    const maxScroll = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);
    const tolerance = 2;

    if (viewport.scrollLeft <= tolerance) return 0;
    if (viewport.scrollLeft >= maxScroll - tolerance) return maxIndex;

    return Math.min(Math.max(Math.round(viewport.scrollLeft / step), 0), maxIndex);
  }

  function getSatisfiedTrackedIndex(section, viewport, slides) {
    const tracked = section?._pdpSatisfiedActiveIndex;
    if (Number.isInteger(tracked) && tracked >= 0 && tracked < slides.length) {
      return tracked;
    }
    return getSatisfiedIndexFromScroll(viewport, slides);
  }

  function getSatisfiedIndexScrollLeft(viewport, slides, index) {
    const maxScroll = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);
    if (index <= 0 || !slides.length) return 0;

    const lastIndex = slides.length - 1;
    if (index >= lastIndex) return maxScroll;

    const { step } = getSatisfiedSlideMetrics(viewport, slides);
    return Math.min(Math.max(index * step, 0), maxScroll);
  }

  function scrollSatisfiedToIndex(section, viewport, slides, index, behavior = "smooth") {
    if (!viewport || !slides.length) return;

    const clampedIndex = Math.min(Math.max(index, 0), slides.length - 1);
    const left = getSatisfiedIndexScrollLeft(viewport, slides, clampedIndex);

    if (section) {
      section._pdpSatisfiedActiveIndex = clampedIndex;
    }

    viewport.scrollTo({ left, behavior });
    syncSatisfiedNavState(section);
  }

  function syncSatisfiedNavState(section) {
    const carousel = getSatisfiedCarousel(section);
    if (!carousel) return;

    const nav = carousel.section?.querySelector(".pdp-loco__satisfied-nav");
    if (!nav) return;

    const prev = nav.querySelector("[data-direction='prev']");
    const next = nav.querySelector("[data-direction='next']");
    const { viewport, slides } = carousel;

    if (!slides.length) return;

    const tolerance = 2;
    const maxScroll = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);
    const atStart = viewport.scrollLeft <= tolerance;
    const atEnd = maxScroll <= tolerance || viewport.scrollLeft >= maxScroll - tolerance;

    if (prev) prev.disabled = atStart;
    if (next) next.disabled = atEnd;
  }

  function bindSatisfiedNavSync(section) {
    const carousel = getSatisfiedCarousel(section);
    if (!carousel || section.dataset.pdpSatisfiedNavScrollBound === "true") return;

    section.dataset.pdpSatisfiedNavScrollBound = "true";
    carousel.viewport.addEventListener(
      "scroll",
      () => {
        syncSatisfiedNavState(section);
      },
      { passive: true }
    );
  }

  function snapSatisfiedToNearest(section) {
    const carousel = getSatisfiedCarousel(section);
    if (!carousel || !carousel.slides.length) return;

    const index = getSatisfiedIndexFromScroll(carousel.viewport, carousel.slides);
    scrollSatisfiedToIndex(carousel.section, carousel.viewport, carousel.slides, index, "smooth");
  }

  function stopSatisfiedAutoplay(section) {
    if (!section?._pdpSatisfiedAutoplayTimer) return;
    clearInterval(section._pdpSatisfiedAutoplayTimer);
    section._pdpSatisfiedAutoplayTimer = null;
  }

  function advanceSatisfiedCarousel(section) {
    const carousel = getSatisfiedCarousel(section);
    if (!carousel || carousel.slides.length <= 1) return;

    const { viewport, slides } = carousel;
    const active = getSatisfiedTrackedIndex(carousel.section, viewport, slides);

    if (active >= slides.length - 1) {
      stopSatisfiedAutoplay(section);
      syncSatisfiedNavState(section);
      return;
    }

    scrollSatisfiedToIndex(carousel.section, viewport, slides, active + 1);
  }

  function startSatisfiedAutoplay(section) {
    if (!section) return;

    stopSatisfiedAutoplay(section);

    const carousel = getSatisfiedCarousel(section);
    if (!carousel || carousel.slides.length <= 1) return;

    section._pdpSatisfiedAutoplayTimer = setInterval(() => {
      advanceSatisfiedCarousel(section);
    }, SATISFIED_AUTOPLAY_MS);
  }

  function bindSatisfiedAutoplayPause(section) {
    if (section.dataset.pdpSatisfiedAutoplayBound === "true") return;
    section.dataset.pdpSatisfiedAutoplayBound = "true";

    const track = section.querySelector("[data-pdp-satisfied-track]");
    if (!track) return;

    track.addEventListener("mouseenter", () => stopSatisfiedAutoplay(section));
    track.addEventListener("mouseleave", () => startSatisfiedAutoplay(section));
    track.addEventListener("focusin", () => stopSatisfiedAutoplay(section));
    track.addEventListener("focusout", () => startSatisfiedAutoplay(section));
  }

  function bindSatisfiedDragScroll(section) {
    const viewport = section?.querySelector("[data-pdp-satisfied-track]");
    if (!viewport || viewport.dataset.pdpSatisfiedDragBound === "true") return;
    viewport.dataset.pdpSatisfiedDragBound = "true";

    let activePointerId = null;
    let activePointerType = "";
    let startX = 0;
    let scrollStart = 0;
    let dragged = false;
    let maxDragDistance = 0;

    const finishDrag = (event) => {
      if (activePointerId === null || (event && event.pointerId !== activePointerId)) return;

      const didDrag = dragged && maxDragDistance > 10;
      activePointerId = null;
      activePointerType = "";
      dragged = false;
      maxDragDistance = 0;
      viewport.classList.remove("is-dragging");
      viewport.style.scrollBehavior = "";

      if (didDrag) {
        section.dataset.pdpSatisfiedSuppressClick = "true";
        snapSatisfiedToNearest(section);
        window.setTimeout(() => {
          section.dataset.pdpSatisfiedSuppressClick = "false";
        }, 320);
      }

      startSatisfiedAutoplay(section);
    };

    viewport.addEventListener(
      "pointerdown",
      (event) => {
        if (event.target.closest("[data-pdp-satisfied-toggle], [data-pdp-satisfied-mute]")) return;
        if (event.pointerType === "mouse" && event.button !== 0) return;

        activePointerId = event.pointerId;
        activePointerType = event.pointerType;
        startX = event.clientX;
        scrollStart = viewport.scrollLeft;
        dragged = false;
        maxDragDistance = 0;
        stopSatisfiedAutoplay(section);
      },
      { passive: true }
    );

    viewport.addEventListener(
      "pointermove",
      (event) => {
        if (activePointerId === null || event.pointerId !== activePointerId) return;

        const deltaX = event.clientX - startX;
        maxDragDistance = Math.max(maxDragDistance, Math.abs(deltaX));
        if (Math.abs(deltaX) < 4) return;

        dragged = true;

        if (activePointerType === "mouse") {
          if (!viewport.classList.contains("is-dragging")) {
            viewport.classList.add("is-dragging");
            viewport.style.scrollBehavior = "auto";

            try {
              viewport.setPointerCapture(event.pointerId);
            } catch (error) {
              /* noop */
            }
          }

          event.preventDefault();
          viewport.scrollLeft = scrollStart - deltaX;
        }
      },
      { passive: false }
    );

    viewport.addEventListener("pointerup", finishDrag);
    viewport.addEventListener("pointercancel", finishDrag);
    viewport.addEventListener("lostpointercapture", finishDrag);

    viewport.addEventListener(
      "scroll",
      () => {
        if (activePointerId !== null && activePointerType !== "mouse") {
          dragged = true;
        }
      },
      { passive: true }
    );

    viewport.addEventListener(
      "dragstart",
      (event) => {
        event.preventDefault();
      },
      true
    );
  }

  function preventSatisfiedMediaDrag(section) {
    section.querySelectorAll(".pdp-loco__satisfied-video, .pdp-loco__satisfied-poster").forEach((node) => {
      node.setAttribute("draggable", "false");
    });
  }

  function setupSatisfiedCarousel(section) {
    const carousel = getSatisfiedCarousel(section);
    if (!carousel || !carousel.slides.length) return false;

    preventSatisfiedMediaDrag(section);
    bindSatisfiedDragScroll(section);
    bindSatisfiedAutoplayPause(section);
    bindSatisfiedNavSync(section);

    if (section.dataset.pdpSatisfiedAligned !== "true") {
      carousel.viewport.scrollLeft = 0;
      section._pdpSatisfiedActiveIndex = 0;
      section.dataset.pdpSatisfiedAligned = "true";
      startSatisfiedAutoplay(section);
    }

    syncSatisfiedNavState(section);

    return true;
  }

  function scrollSatisfiedCarousel(section, direction) {
    const carousel = getSatisfiedCarousel(section);
    if (!carousel || !carousel.slides.length) return false;

    const resolvedSection = carousel.section || getSatisfiedCarouselSection(section);
    if (!resolvedSection) return false;

    stopSatisfiedAutoplay(resolvedSection);

    const { viewport, slides } = carousel;
    const active = getSatisfiedTrackedIndex(resolvedSection, viewport, slides);
    const nextIndex = active + direction;

    if (nextIndex < 0 || nextIndex >= slides.length) {
      syncSatisfiedNavState(resolvedSection);
      startSatisfiedAutoplay(resolvedSection);
      return false;
    }

    scrollSatisfiedToIndex(resolvedSection, viewport, slides, nextIndex);

    let autoplayRestarted = false;
    const restartAutoplay = () => {
      if (autoplayRestarted) return;
      autoplayRestarted = true;
      startSatisfiedAutoplay(resolvedSection);
    };

    if ("onscrollend" in window) {
      viewport.addEventListener("scrollend", restartAutoplay, { once: true });
      window.setTimeout(restartAutoplay, 600);
    } else {
      window.setTimeout(restartAutoplay, 450);
    }

    return true;
  }

  function initSatisfiedCustomers(root) {
    root.querySelectorAll(".pdp-loco__satisfied-customers").forEach((section) => {
      if (section.dataset.pdpSatisfiedBound === "true") return;
      section.dataset.pdpSatisfiedBound = "true";

      section.querySelectorAll(".pdp-loco__satisfied-card.is-file .pdp-loco__satisfied-video").forEach((video) => {
        const card = video.closest(".pdp-loco__satisfied-card");
        video.addEventListener("play", () => updateSatisfiedCardToggle(card, false));
        video.addEventListener("pause", () => updateSatisfiedCardToggle(card, true));
        video.addEventListener("volumechange", () => updateSatisfiedCardMute(card, video.muted));

        updateSatisfiedCardMute(card, video.muted);
        updateSatisfiedCardToggle(card, true);

        // Autoplay diferido: el src se asigna y el vídeo arranca (silenciado) al acercarse al viewport,
        // y se pausa al salir. Así no se descargan todos los vídeos de clientes al cargar la página.
        const lazyPlay = () => {
          const source = video.dataset.src || video.getAttribute("src");
          if (!source) return;
          if (!video.src) video.src = source;
          const playPromise = video.play();
          if (playPromise?.then) {
            playPromise
              .then(() => updateSatisfiedCardToggle(card, false))
              .catch(() => updateSatisfiedCardToggle(card, true));
          } else {
            updateSatisfiedCardToggle(card, video.paused);
          }
        };

        if ("IntersectionObserver" in window) {
          const visibilityObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  if (card.dataset.pdpUserPaused !== "true") lazyPlay();
                } else if (!video.paused) {
                  video.pause();
                }
              });
            },
            { rootMargin: "200px 0px", threshold: 0.01 }
          );
          visibilityObserver.observe(card);
          card._pdpSatisfiedVisibilityObserver = visibilityObserver;
        } else {
          lazyPlay();
        }
      });

      section.querySelectorAll(".pdp-loco__satisfied-card").forEach((card) => {
        bindSatisfiedCardOpen(card);

        const toggle = card.querySelector("[data-pdp-satisfied-toggle]");
        if (toggle && toggle.dataset.pdpBound !== "true") {
          toggle.dataset.pdpBound = "true";
          toggle.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (card.classList.contains("is-url")) {
              openSatisfiedLightbox(card);
              return;
            }

            toggleSatisfiedCardPlayback(card);
          });
        }

        const muteToggle = card.querySelector("[data-pdp-satisfied-mute]");
        if (muteToggle && muteToggle.dataset.pdpBound !== "true") {
          muteToggle.dataset.pdpBound = "true";
          muteToggle.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            toggleSatisfiedCardMute(card);
          });
        }
      });

      setupSatisfiedCarousel(section);

      if (window.ResizeObserver) {
        const track = section.querySelector("[data-pdp-satisfied-track]");
        if (track && !section._pdpSatisfiedResizeObserver) {
          section._pdpSatisfiedResizeObserver = new ResizeObserver(() => {
            section.dataset.pdpSatisfiedAligned = "false";
            stopSatisfiedAutoplay(section);
            setupSatisfiedCarousel(section);
          });
          section._pdpSatisfiedResizeObserver.observe(track);
        }
      }
    });
  }

  const REVIEWS_AUTOPLAY_MS = 5000;

  function getReviewsCarousel(section) {
    const holder = section?.querySelector(".reputon-carousel-holder");
    if (!holder) return null;

    let viewport = null;
    for (const child of holder.children) {
      if (child.tagName !== "DIV") continue;
      const candidateTrack = child.firstElementChild;
      if (candidateTrack?.tagName === "DIV") {
        viewport = child;
        break;
      }
    }
    viewport = viewport || holder.querySelector(":scope > div");

    const track = viewport?.firstElementChild;
    if (!viewport || !track) return null;

    const slides = [...track.children].filter(
      (slide) =>
        slide.offsetParent !== null &&
        getComputedStyle(slide).display !== "none" &&
        !slide.querySelector(".reputon-summary-review")
    );

    return { viewport, track, slides };
  }

  function getActiveReviewSlideIndex(viewport, slides) {
    const scrollLeft = viewport.scrollLeft;
    let activeIndex = 0;
    let minDistance = Infinity;

    slides.forEach((slide, index) => {
      const track = slide.parentElement;
      const left = track ? slide.offsetLeft - track.offsetLeft : slide.offsetLeft;
      const distance = Math.abs(left - scrollLeft);
      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = index;
      }
    });

    return activeIndex;
  }

  function scrollReviewsToSlide(viewport, slide) {
    if (!viewport || !slide) return;

    const track = slide.parentElement;
    const left = track ? slide.offsetLeft - track.offsetLeft : slide.offsetLeft;
    viewport.scrollTo({ left: Math.max(left, 0), behavior: "smooth" });

    const section = viewport.closest(".pdp-loco__google-reviews, .pdp-loco__app-section");
    if (section) {
      syncReviewsNavState(section);
    }
  }

  function getReviewsNav(section) {
    const heading = section?.previousElementSibling;
    if (heading?.classList?.contains("pdp-loco__section-heading")) {
      return heading.querySelector(".pdp-loco__section-heading-nav");
    }

    return section?.querySelector(".pdp-loco__section-heading-nav") || null;
  }

  function syncReviewsNavState(section) {
    const carousel = getReviewsCarousel(section);
    const nav = getReviewsNav(section);
    if (!carousel || !nav) return;

    const prev = nav.querySelector("[data-direction='prev']");
    const next = nav.querySelector("[data-direction='next']");
    const { viewport, slides } = carousel;

    if (!slides.length) return;

    const tolerance = 2;
    const maxScroll = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);
    const atStart = viewport.scrollLeft <= tolerance;
    const atEnd = maxScroll <= tolerance || viewport.scrollLeft >= maxScroll - tolerance;

    if (prev) prev.disabled = atStart;
    if (next) next.disabled = atEnd;
  }

  function bindReviewsNavSync(section) {
    const carousel = getReviewsCarousel(section);
    if (!carousel || section.dataset.pdpReviewsNavScrollBound === "true") return;

    section.dataset.pdpReviewsNavScrollBound = "true";
    carousel.viewport.addEventListener(
      "scroll",
      () => {
        syncReviewsNavState(section);
      },
      { passive: true }
    );
  }

  const REVIEWS_TOGGLE_SYNC_MS = 220;

  function getReviewCards(section) {
    return section.querySelectorAll(
      ".reputon-single-review.reputon-card:not(.reputon-summary-review)"
    );
  }

  function getReviewCardUrl(card) {
    if (!card) return null;

    const candidates = card.querySelectorAll(
      'a.reputon-link[href]:not(.reputon-show-more):not(.reputon-leave-review), .reputon-review-header a[href], .reputon-top-part a[href]:not(.reputon-show-more):not(.reputon-leave-review), a[href*="google.com/maps"], a[href*="g.page"], a[href*="search.google.com/local/reviews"]'
    );

    for (const anchor of candidates) {
      const href = anchor.getAttribute("href")?.trim();
      if (href && href !== "#" && !href.startsWith("javascript:")) return href;
    }

    return null;
  }

  function isReviewCardInteractiveTarget(target) {
    return Boolean(target?.closest?.(".reputon-show-more, .reputon-leave-review"));
  }

  function activateReviewCardNavigation(section, target) {
    if (!section.closest(".pdp-loco__info")) return false;
    if (section.dataset.pdpReviewsSuppressClick === "true") return false;

    const card = target?.closest?.(
      ".reputon-single-review.reputon-card:not(.reputon-summary-review)"
    );
    if (!card || !section.contains(card)) return false;
    if (isReviewCardInteractiveTarget(target)) return false;

    const directLink = target.closest(
      'a[href]:not(.reputon-show-more):not(.reputon-leave-review)'
    );
    if (directLink) return false;

    const url = getReviewCardUrl(card);
    if (!url) return false;

    window.open(url, "_blank", "noopener,noreferrer");
    return true;
  }

  function bindReviewCardClickNavigation(section) {
    if (!section.closest(".pdp-loco__info")) return;
    section.dataset.pdpReviewCardClickBound = "true";
  }

  function getReviewContainers(section) {
    return section.querySelectorAll(
      ".reputon-single-review.reputon-card:not(.reputon-summary-review) .reputon-container"
    );
  }

  function isReviewCardExpanded(card) {
    const toggle = card.querySelector(".reputon-show-more");
    if (toggle) {
      const label = (toggle.querySelector("span") || toggle).textContent.trim().toLowerCase();
      if (label.includes("menos") || label.includes("less")) return true;
      if (label.includes("más") || label.includes("more") || label.includes("mas ")) return false;
    }

    const text = card.querySelector(".reputon-text");
    if (!text) return false;

    const clamp = getComputedStyle(text).webkitLineClamp;
    if (clamp === "none" || clamp === "unset") return true;

    const clampNum = parseInt(clamp, 10);
    return Number.isNaN(clampNum) || clampNum <= 0;
  }

  function hasExpandedReviewCards(section) {
    return [...getReviewCards(section)].some(isReviewCardExpanded);
  }

  function clearReviewsCardHeights(section) {
    if (!section) return;

    section
      .querySelectorAll(
        ".reputon-carousel-holder > div > div > div:not(:has(.reputon-summary-review))"
      )
      .forEach((slide) => {
        slide.style.removeProperty("min-height");
        slide.style.removeProperty("height");
      });

    getReviewCards(section).forEach((shell) => {
      shell.style.removeProperty("min-height");
      shell.style.removeProperty("height");
      shell.style.removeProperty("align-items");
    });

    getReviewContainers(section).forEach((card) => {
      card.classList.remove("pdp-reviews-height-sync");
      card.style.removeProperty("min-height");
      card.style.removeProperty("height");
    });

    delete section.dataset.pdpReviewsMaxHeight;
    delete section.dataset.pdpReviewsCollapsedHeight;
  }

  function rememberReviewsCollapsedHeight(section) {
    const height = section.dataset.pdpReviewsCollapsedHeight || section.dataset.pdpReviewsMaxHeight;
    if (height) {
      section.dataset.pdpReviewsCollapsedHeight = height;
      return;
    }

    let maxHeight = 0;
    getReviewContainers(section).forEach((container) => {
      maxHeight = Math.max(
        maxHeight,
        container.scrollHeight,
        container.getBoundingClientRect().height
      );
    });

    if (maxHeight > 0) {
      section.dataset.pdpReviewsCollapsedHeight = `${Math.ceil(maxHeight)}px`;
    }
  }

  function syncReviewsCarouselHeights(section) {
    if (!section || section._pdpReviewsHeightSyncing) return;

    const containers = [...getReviewContainers(section)];
    if (!containers.length) return;

    section._pdpReviewsHeightSyncing = true;

    try {
      const expanded = hasExpandedReviewCards(section);
      const collapsedTarget = section.dataset.pdpReviewsCollapsedHeight;
      let heightPx = "";

      containers.forEach((container) => container.classList.add("pdp-reviews-height-sync"));

      if (!expanded && collapsedTarget) {
        heightPx = collapsedTarget;
        containers.forEach((container) => {
          container.classList.remove("pdp-reviews-height-sync");
          container.style.setProperty("min-height", heightPx, "important");
        });
      } else if (expanded) {
        let maxHeight = 0;
        containers.forEach((container) => {
          maxHeight = Math.max(
            maxHeight,
            container.scrollHeight,
            container.getBoundingClientRect().height
          );
        });

        if (maxHeight <= 0) {
          containers.forEach((container) => container.classList.remove("pdp-reviews-height-sync"));
          return;
        }

        heightPx = `${Math.ceil(maxHeight)}px`;
        section.dataset.pdpReviewsMaxHeight = heightPx;

        containers.forEach((container) => {
          container.classList.remove("pdp-reviews-height-sync");
          container.style.setProperty("min-height", heightPx, "important");
        });
      } else {
        containers.forEach((container) => container.style.removeProperty("min-height"));
        if (containers[0]) {
          void containers[0].offsetHeight;
        }

        let maxHeight = 0;
        containers.forEach((container) => {
          maxHeight = Math.max(
            maxHeight,
            container.scrollHeight,
            container.getBoundingClientRect().height
          );
        });

        if (maxHeight <= 0) {
          containers.forEach((container) => container.classList.remove("pdp-reviews-height-sync"));
          return;
        }

        heightPx = `${Math.ceil(maxHeight)}px`;
        section.dataset.pdpReviewsMaxHeight = heightPx;
        section.dataset.pdpReviewsCollapsedHeight = heightPx;

        containers.forEach((container) => {
          container.classList.remove("pdp-reviews-height-sync");
          container.style.setProperty("min-height", heightPx, "important");
        });
      }

      if (expanded) {
        section._pdpReviewsCardResizeObserver?.disconnect();
      } else {
        bindReviewsCardResizeObserver(section);
      }
    } finally {
      section._pdpReviewsHeightSyncing = false;
    }
  }

  function requestReviewsHeightSync(section, delay = 0) {
    if (!section) return;

    const run = () => {
      scheduleReviewsHeightSync(section);
    };

    if (delay > 0) {
      window.setTimeout(run, delay);
      return;
    }

    run();
  }

  function bindReviewsCardResizeObserver(section) {
    if (!window.ResizeObserver || !section) return;

    let debounceTimer;

    if (!section._pdpReviewsCardResizeObserver) {
      section._pdpReviewsCardResizeObserver = new ResizeObserver(() => {
        if (section._pdpReviewsHeightSyncing) return;

        clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(() => {
          if (section._pdpReviewsHeightSyncing) return;
          scheduleReviewsHeightSync(section);
        }, 150);
      });
    }

    const observer = section._pdpReviewsCardResizeObserver;
    observer.disconnect();

    getReviewContainers(section).forEach((card) => observer.observe(card));
  }

  function scheduleReviewsToggleHeightSync(section) {
    clearTimeout(section._pdpReviewsToggleTimer);
    section._pdpReviewsToggleTimer = window.setTimeout(() => {
      scheduleReviewsHeightSync(section);
    }, REVIEWS_TOGGLE_SYNC_MS);
  }

  function bindReviewsToggleHeightSync(section) {
    if (section.dataset.pdpReviewsToggleBound === "true") return;
    section.dataset.pdpReviewsToggleBound = "true";

    section.addEventListener(
      "click",
      (event) => {
        const toggle = event.target.closest(
          ".reputon-show-more, .reputon-single-review:not(.reputon-summary-review) .reputon-text"
        );
        if (!toggle) return;

        rememberReviewsCollapsedHeight(section);
        scheduleReviewsToggleHeightSync(section);
      },
      true
    );
  }

  function scheduleReviewsHeightSync(section) {
    if (section._pdpReviewsHeightRaf) {
      cancelAnimationFrame(section._pdpReviewsHeightRaf);
    }

    section._pdpReviewsHeightRaf = requestAnimationFrame(() => {
      section._pdpReviewsHeightRaf = requestAnimationFrame(() => {
        section._pdpReviewsHeightRaf = null;
        syncReviewsCarouselHeights(section);
      });
    });
  }

  function snapReviewsToNearest(section) {
    const carousel = getReviewsCarousel(section);
    if (!carousel || !carousel.slides.length) return;

    const index = getActiveReviewSlideIndex(carousel.viewport, carousel.slides);
    scrollReviewsToSlide(carousel.viewport, carousel.slides[index]);
  }

  function silenceEmblaReviewsHandlers(viewport) {
    if (!viewport || viewport.dataset.pdpReviewsEmblaSilenced === "true") return;
    viewport.dataset.pdpReviewsEmblaSilenced = "true";

    const stopEmbla = (event) => {
      event.stopImmediatePropagation();
    };

    ["touchstart", "touchmove", "mousedown"].forEach((type) => {
      viewport.addEventListener(type, stopEmbla, { capture: true, passive: true });
    });
  }

  function bindReviewsDragScroll(section) {
    const carousel = getReviewsCarousel(section);
    const viewport = carousel?.viewport;
    if (!viewport || viewport.dataset.pdpReviewsDragBound === "true") return;
    viewport.dataset.pdpReviewsDragBound = "true";

    silenceEmblaReviewsHandlers(viewport);

    let activePointerId = null;
    let activePointerType = "";
    let startX = 0;
    let startY = 0;
    let scrollStart = 0;
    let dragged = false;
    let maxDragDistance = 0;

    const finishDrag = (event) => {
      if (activePointerId === null || (event && event.pointerId !== activePointerId)) return;

      const didDrag = dragged && maxDragDistance > 10;
      const pointerTarget = event?.target;
      activePointerId = null;
      activePointerType = "";
      dragged = false;
      maxDragDistance = 0;
      viewport.classList.remove("is-dragging");
      viewport.style.scrollBehavior = "";

      if (didDrag) {
        section.dataset.pdpReviewsSuppressClick = "true";
        snapReviewsToNearest(section);
        window.setTimeout(() => {
          section.dataset.pdpReviewsSuppressClick = "false";
        }, 320);
      } else if (pointerTarget) {
        activateReviewCardNavigation(section, pointerTarget);
      }

      startReviewsAutoplay(section);
    };

    viewport.addEventListener(
      "pointerdown",
      (event) => {
        if (event.target.closest(".reputon-show-more")) return;
        if (event.pointerType === "mouse" && event.button !== 0) return;

        activePointerId = event.pointerId;
        activePointerType = event.pointerType;
        startX = event.clientX;
        startY = event.clientY;
        scrollStart = viewport.scrollLeft;
        dragged = false;
        maxDragDistance = 0;
        stopReviewsAutoplay(section);
      },
      { passive: true }
    );

    viewport.addEventListener(
      "pointermove",
      (event) => {
        if (activePointerId === null || event.pointerId !== activePointerId) return;

        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        maxDragDistance = Math.max(maxDragDistance, Math.abs(deltaX), Math.abs(deltaY));

        if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return;
        if (Math.abs(deltaX) <= Math.abs(deltaY)) return;

        dragged = true;

        if (activePointerType === "mouse") {
          if (!viewport.classList.contains("is-dragging")) {
            viewport.classList.add("is-dragging");
            viewport.style.scrollBehavior = "auto";

            try {
              viewport.setPointerCapture(event.pointerId);
            } catch (error) {
              /* noop */
            }
          }

          event.preventDefault();
          viewport.scrollLeft = scrollStart - deltaX;
        }
      },
      { passive: false }
    );

    viewport.addEventListener("pointerup", finishDrag);
    viewport.addEventListener("pointercancel", finishDrag);
    viewport.addEventListener("lostpointercapture", finishDrag);

    viewport.addEventListener(
      "scroll",
      () => {
        if (activePointerId !== null && activePointerType !== "mouse") {
          if (Math.abs(viewport.scrollLeft - scrollStart) > 6) {
            dragged = true;
          }
        }
      },
      { passive: true }
    );

    viewport.addEventListener(
      "dragstart",
      (event) => {
        event.preventDefault();
      },
      true
    );
  }

  function preventReviewsMediaDrag(section) {
    section
      .querySelectorAll(
        ".reputon-single-review.reputon-card:not(.reputon-summary-review) img, .reputon-single-review.reputon-card:not(.reputon-summary-review) a"
      )
      .forEach((node) => {
        node.setAttribute("draggable", "false");
      });
  }

  function setupReviewsCarousel(section) {
    const carousel = getReviewsCarousel(section);
    if (!carousel?.slides.length) return false;

    preventReviewsMediaDrag(section);
    bindReviewsDragScroll(section);
    bindReviewsNavSync(section);
    syncReviewsNavState(section);
    return true;
  }

  function neutralizeReviewsViewportScroll(section) {
    const carousel = getReviewsCarousel(section);
    const viewport = carousel?.viewport;
    const track = carousel?.track;
    if (!viewport || viewport.dataset.pdpReviewsScrollPinned === "true") return;
    viewport.dataset.pdpReviewsScrollPinned = "true";

    const pinScroll = () => {
      viewport.style.setProperty("overflow-x", "scroll", "important");
      viewport.style.setProperty("overflow-y", "hidden", "important");
      viewport.style.setProperty("touch-action", "pan-x", "important");
      viewport.style.setProperty("-webkit-overflow-scrolling", "touch", "important");
      viewport.style.setProperty("width", "100%", "important");

      if (track) {
        track.style.setProperty("touch-action", "pan-x", "important");
        track.style.setProperty("will-change", "auto", "important");
      }
    };

    pinScroll();
    new MutationObserver(pinScroll).observe(viewport, {
      attributes: true,
      attributeFilter: ["style"],
    });

    if (track) {
      new MutationObserver(pinScroll).observe(track, {
        attributes: true,
        attributeFilter: ["style"],
      });
    }
  }

  function neutralizeEmblaTrack(section) {
    const carousel = getReviewsCarousel(section);
    const track = carousel?.track;
    if (!track || track.dataset.pdpNativeScroll === "true") return;

    track.dataset.pdpNativeScroll = "true";

    const pinTransform = () => {
      track.style.setProperty("transform", "none", "important");
      track.style.setProperty("translate", "none", "important");
      track.style.setProperty("margin-left", "0", "important");
    };

    pinTransform();
    new MutationObserver(pinTransform).observe(track, {
      attributes: true,
      attributeFilter: ["style"],
    });
  }

  function hideSummarySlides(section) {
    section.querySelectorAll(".reputon-summary-review").forEach((card) => {
      const slide = card.parentElement;
      if (slide) {
        slide.style.setProperty("display", "none", "important");
      }
    });
  }

  function resetReviewsCarouselStart(section) {
    const carousel = getReviewsCarousel(section);
    if (!carousel) return;
    carousel.viewport.scrollLeft = 0;
    syncReviewsNavState(section);
  }

  function stopReviewsAutoplay(section) {
    if (!section?._pdpReviewsAutoplayTimer) return;
    clearInterval(section._pdpReviewsAutoplayTimer);
    section._pdpReviewsAutoplayTimer = null;
  }

  function startReviewsAutoplay(section) {
    if (!section) return;

    stopReviewsAutoplay(section);

    section._pdpReviewsAutoplayTimer = setInterval(() => {
      const carousel = getReviewsCarousel(section);
      if (!carousel || carousel.slides.length <= 1) return;

      const active = getActiveReviewSlideIndex(carousel.viewport, carousel.slides);

      if (active >= carousel.slides.length - 1) {
        stopReviewsAutoplay(section);
        syncReviewsNavState(section);
        return;
      }

      scrollReviewsToSlide(carousel.viewport, carousel.slides[active + 1]);
    }, REVIEWS_AUTOPLAY_MS);
  }

  function bindReviewsAutoplayPause(section) {
    if (section.dataset.pdpReviewsHoverBound === "true") return;
    section.dataset.pdpReviewsHoverBound = "true";

    section.addEventListener("mouseenter", () => stopReviewsAutoplay(section));
    section.addEventListener("mouseleave", () => startReviewsAutoplay(section));
    section.addEventListener("focusin", () => stopReviewsAutoplay(section));
    section.addEventListener("focusout", () => startReviewsAutoplay(section));
  }

  function scrollReviewsCarousel(section, direction) {
    const carousel = getReviewsCarousel(section);
    if (!carousel || !carousel.slides.length) return false;

    stopReviewsAutoplay(section);

    const active = getActiveReviewSlideIndex(carousel.viewport, carousel.slides);
    const nextIndex = active + direction;

    if (nextIndex < 0 || nextIndex >= carousel.slides.length) {
      syncReviewsNavState(section);
      startReviewsAutoplay(section);
      return false;
    }

    scrollReviewsToSlide(carousel.viewport, carousel.slides[nextIndex]);
    startReviewsAutoplay(section);
    return true;
  }

  function scrollReputonCarousel(container, direction) {
    const section =
      container?.closest(".pdp-loco__google-reviews") ||
      (container?.classList?.contains("pdp-loco__google-reviews") ? container : null);

    if (!section) return false;
    return scrollReviewsCarousel(section, direction);
  }

  function getCarouselScrollTarget(container) {
    if (!container) return null;

    const reputonHolder = container.querySelector(".reputon-carousel-holder");
    if (reputonHolder) return reputonHolder;

    const selectors = [
      ".pdp-loco__satisfied-track",
      ".pdp-loco__complete-track",
      '[class*="reviews-list"]',
      ".reputon-carousel-holder",
      '[class*="slider"]',
      '[class*="scroll-container"]',
    ];

    for (const selector of selectors) {
      const candidate = container.querySelector(selector);
      if (candidate && candidate.scrollWidth > candidate.clientWidth) {
        return candidate;
      }
    }

    return container;
  }

  function scrollCarousel(container, direction) {
    if (!container) return;

    if (container.classList?.contains("pdp-loco__satisfied-customers")) {
      scrollSatisfiedCarousel(container, direction);
      return;
    }

    const satisfiedSection = container.closest?.(".pdp-loco__satisfied-customers");
    if (satisfiedSection) {
      scrollSatisfiedCarousel(satisfiedSection, direction);
      return;
    }

    const hasReputonWidget = container.querySelector(
      '[class*="reputon-google-reviews-widget"]'
    );
    if (hasReputonWidget) {
      scrollReputonCarousel(container, direction);
      return;
    }

    if (scrollReputonCarousel(container, direction)) return;

    const scrollTarget = getCarouselScrollTarget(container);
    if (!scrollTarget) return;

    const firstCard = scrollTarget.querySelector(
      ".pdp-loco__satisfied-card, .pdp-loco__complete-card, .reputon-single-review.reputon-card:not(.reputon-summary-review), [class*='review-card']"
    );
    const gap = 8;
    const amount = firstCard
      ? firstCard.getBoundingClientRect().width + gap
      : Math.max(scrollTarget.clientWidth * 0.85, 200);

    scrollTarget.scrollBy({
      left: direction * amount,
      behavior: "smooth",
    });
  }

  const REPUTON_CHROME_SELECTORS = [
    ".reputon-reviews-head",
    ".reputon-static-plate",
    ".reputon-summary-review",
    ".reputon-bottom-navigation-btns",
    ".reputon-swiper-button-prev",
    ".reputon-swiper-button-next",
    ".reputon-leave-review",
    ".reputon-buttons-holder",
    '[class*="widget-header"]',
    '[class*="WidgetHeader"]',
    '[class*="write-review"]',
    ".reputon-write-review-button",
  ].join(", ");

  function initGoogleReviews(root) {
    root
      .querySelectorAll(
        ".pdp-loco__google-reviews, .pdp-loco__section-heading + .pdp-loco__app-section"
      )
      .forEach((section) => {
        const widget = section.querySelector('[class*="reputon-google-reviews-widget"]');
        if (!widget) return;

        section.classList.add("pdp-loco__google-reviews");
        if (section.dataset.pdpReviewsBound === "true") return;
        section.dataset.pdpReviewsBound = "true";

        const applyReviewsLayout = () => {
          section.querySelectorAll(REPUTON_CHROME_SELECTORS).forEach((node) => {
            node.style.setProperty("display", "none", "important");
          });

          hideSummarySlides(section);
          neutralizeReviewsViewportScroll(section);
          neutralizeEmblaTrack(section);

          section
            .querySelectorAll(
              ".reputon-single-review.reputon-card:not(.reputon-summary-review) .reputon-container"
            )
            .forEach((card) => {
              card.style.setProperty(
                "background",
                "var(--Brand-Colors-neutral-300, #EBEDEF)",
                "important"
              );
              card.style.setProperty("border-radius", "13.45px", "important");
              card.style.setProperty("padding", "20.17px", "important");
              card.style.setProperty("gap", "10.09px", "important");
              card.style.setProperty("aspect-ratio", "auto", "important");
              card.style.setProperty("height", "auto", "important");
            });

          const cardCount = section.querySelectorAll(
            ".reputon-single-review.reputon-card:not(.reputon-summary-review) .reputon-container"
          ).length;
          const previousCardCount = parseInt(section.dataset.pdpReviewsCardCount || "0", 10);
          if (cardCount !== previousCardCount) {
            section.dataset.pdpReviewsCardCount = String(cardCount);
            section.dataset.pdpReviewsMaxHeight = "";
            delete section.dataset.pdpReviewsCollapsedHeight;
          }

          if (cardCount > 0 && !section.dataset.pdpReviewsMaxHeight) {
            scheduleReviewsHeightSync(section);
          }

          const carousel = getReviewsCarousel(section);
          if (!carousel?.slides.length) return;

          if (section.dataset.pdpReviewsAligned !== "true") {
            resetReviewsCarouselStart(section);
            section.dataset.pdpReviewsAligned = "true";
          }

          if (section.dataset.pdpReviewsReady !== "true") {
            section.dataset.pdpReviewsReady = "true";
            bindReviewsAutoplayPause(section);
            setupReviewsCarousel(section);
            startReviewsAutoplay(section);
          } else {
            setupReviewsCarousel(section);
          }
        };

        applyReviewsLayout();
        const observer = new MutationObserver((mutations) => {
          const structuralChange = mutations.some((mutation) => {
            if (mutation.type !== "childList") return false;
            const target = mutation.target;
            return target === widget || target.closest('[class*="reputon-google-reviews-widget"]') === widget;
          });

          const reviewContentChanged = mutations.some((mutation) => {
            const target = mutation.target;
            if (!(target instanceof Node)) return false;

            if (mutation.type === "characterData") {
              return !!target.parentElement?.closest(".reputon-single-review:not(.reputon-summary-review)");
            }

            if (mutation.type === "childList") {
              return !!target.closest?.(".reputon-single-review:not(.reputon-summary-review)");
            }

            return false;
          });

          if (structuralChange) {
            applyReviewsLayout();
            return;
          }

          if (reviewContentChanged) {
            scheduleReviewsToggleHeightSync(section);
          }
        });
        observer.observe(widget, { childList: true, subtree: true, characterData: true });

        bindReviewCardClickNavigation(section);
        bindReviewsToggleHeightSync(section);

        if (window.ResizeObserver && !section._pdpReviewsResizeObserver) {
          section._pdpReviewsResizeObserver = new ResizeObserver(() => {
            if (section._pdpReviewsHeightSyncing) return;

            scheduleReviewsHeightSync(section);
            syncReviewsNavState(section);
          });

          section._pdpReviewsResizeObserver.observe(widget);
        }
      });
  }

  function resolveCarouselSectionFromNav(nav, root) {
    if (nav.dataset.pdpCarousel === "satisfied") {
      const satisfiedSection = nav.closest(".pdp-loco__satisfied-customers");
      if (satisfiedSection) {
        return { section: satisfiedSection, type: "satisfied" };
      }
    }

    const satisfiedSection = nav.closest(".pdp-loco__satisfied-customers");
    if (satisfiedSection) {
      return { section: satisfiedSection, type: "satisfied" };
    }

    const sectionId = nav.dataset.targetId;
    if (sectionId) {
      const appSection = root.querySelector(
        `.pdp-loco__app-section[data-carousel-id="${sectionId}"]`
      );
      if (appSection) {
        const isGoogleReviews =
          appSection.classList.contains("pdp-loco__google-reviews") ||
          !!appSection.querySelector('[class*="reputon-google-reviews-widget"]');
        return { section: appSection, type: isGoogleReviews ? "reviews" : "generic" };
      }
    }

    if (nav.dataset.carouselSibling === "next") {
      const heading = nav.closest(".pdp-loco__section-heading");
      const nextSibling = heading?.nextElementSibling;

      if (nextSibling?.classList?.contains("pdp-loco__app-section")) {
        const isGoogleReviews =
          nextSibling.classList.contains("pdp-loco__google-reviews") ||
          !!nextSibling.querySelector('[class*="reputon-google-reviews-widget"]');
        return { section: nextSibling, type: isGoogleReviews ? "reviews" : "generic" };
      }
    }

    const appSection = nav.closest(".pdp-loco__app-section");
    if (appSection) {
      const isGoogleReviews =
        appSection.classList.contains("pdp-loco__google-reviews") ||
        !!appSection.querySelector('[class*="reputon-google-reviews-widget"]');
      return { section: appSection, type: isGoogleReviews ? "reviews" : "generic" };
    }

    return null;
  }

  function bindSectionHeadings(root) {
    root.querySelectorAll(".pdp-loco__section-heading-nav").forEach((nav) => {
      if (nav.dataset.pdpBound === "true") return;
      nav.dataset.pdpBound = "true";

      const target = resolveCarouselSectionFromNav(nav, root);
      if (!target?.section) return;

      const { section: appSection, type } = target;
      const prev = nav.querySelector("[data-direction='prev']");
      const next = nav.querySelector("[data-direction='next']");

      if (prev) {
        prev.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (type === "reviews") {
            scrollReputonCarousel(appSection, -1);
            return;
          }
          if (type === "satisfied") {
            scrollSatisfiedCarousel(appSection, -1);
            return;
          }
          scrollCarousel(appSection, -1);
        });
      }
      if (next) {
        next.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (type === "reviews") {
            scrollReputonCarousel(appSection, 1);
            return;
          }
          if (type === "satisfied") {
            scrollSatisfiedCarousel(appSection, 1);
            return;
          }
          scrollCarousel(appSection, 1);
        });
      }
    });
  }

  function getShopifyChatNodes() {
    const nodes = new Set();
    const selectors = ["#shopify-chat", "shopify-chat", "#ShopifyChat", "inbox-online-store-chat"];

    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((node) => nodes.add(node));
    });

    const root = document.getElementById("shopify-chat");
    if (root) {
      nodes.add(root);
      root.querySelectorAll("shopify-chat, iframe").forEach((node) => nodes.add(node));
    }

    return Array.from(nodes);
  }

  function hasShopifyChat() {
    return getShopifyChatNodes().length > 0;
  }

  function isChatHostOpen(host) {
    if (!host) return false;

    const openAttrs = ["is-open", "isopen", "isOpen", "open", "data-open", "expanded"];
    for (const attr of openAttrs) {
      const value = host.getAttribute(attr);
      if (value === "true" || value === "" || value === "open") return true;
    }

    return host.classList.contains("is-open") || host.classList.contains("open");
  }

  function isChatIframeExpanded(iframe) {
    if (!iframe) return false;

    const iframeOpen =
      iframe.getAttribute("isopen") ||
      iframe.getAttribute("isOpen") ||
      iframe.getAttribute("is-open") ||
      "";
    if (/^true$/i.test(iframeOpen)) return true;

    const styleAttr = iframe.getAttribute("style") || "";
    if (/min-height|minHeight/i.test(styleAttr)) return true;
    if (/\bheight:\s*(?:[2-9]\d{2}|\d{4,})px/i.test(styleAttr)) return true;
    if (/left:\s*0/i.test(styleAttr) && /width:\s*100/i.test(styleAttr)) return true;

    const inline = iframe.style;
    const minHeight = parseFloat(inline.minHeight) || 0;
    const height = parseFloat(inline.height) || 0;
    if (minHeight > 80 || height > 120) return true;

    let computed = null;
    try {
      computed = window.getComputedStyle(iframe);
    } catch (error) {
      computed = null;
    }

    if (computed) {
      const computedHeight = parseFloat(computed.height) || 0;
      const computedMinHeight = parseFloat(computed.minHeight) || 0;
      if (computedHeight > 120 || computedMinHeight > 80) return true;
    }

    const rect = iframe.getBoundingClientRect();
    if (iframe.id === "dummy-chat-button-iframe") {
      return rect.height > 72 || rect.width > 72;
    }

    if (rect.height > 120) return true;
    if (rect.width > window.innerWidth * 0.65 && rect.height > 80) return true;

    return false;
  }

  function isShopifyInboxChatOpen() {
    for (const host of document.querySelectorAll("inbox-online-store-chat, shopify-chat, #ShopifyChat")) {
      if (isChatHostOpen(host)) return true;
    }

    for (const iframe of document.querySelectorAll("#shopify-chat iframe, inbox-online-store-chat iframe")) {
      if (isChatIframeExpanded(iframe)) return true;
    }

    const chatRoot = document.getElementById("shopify-chat");
    if (chatRoot) {
      const rect = chatRoot.getBoundingClientRect();
      const isMobile = window.matchMedia(MOBILE_QUERY).matches;
      if (isMobile && rect.height > window.innerHeight * 0.35) return true;
      if (!isMobile && rect.height > 280 && rect.width > 280) return true;
    }

    return false;
  }

  function applyChatOpenStackOverride(isOpen) {
    let styleEl = document.getElementById("pdp-loco-chat-open-stack");

    if (!isOpen) {
      styleEl?.remove();
      return;
    }

    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = "pdp-loco-chat-open-stack";
      document.head.appendChild(styleEl);
    }

    styleEl.textContent = `
      body.template-product .pdp-loco .product-add-to-cart-sticky.sticky--visible,
      body.template-product product-add-to-cart-sticky.product-add-to-cart-sticky.sticky--visible {
        z-index: 10 !important;
        pointer-events: none !important;
      }

      body.template-product #shopify-chat,
      body.template-product shopify-chat,
      body.template-product inbox-online-store-chat {
        z-index: 2147483000 !important;
        pointer-events: auto !important;
      }

      body.template-product #shopify-chat iframe,
      body.template-product inbox-online-store-chat iframe {
        pointer-events: auto !important;
      }

      @media only screen and (max-width: 767px) {
        body.template-product #shopify-chat,
        body.template-product shopify-chat,
        body.template-product inbox-online-store-chat {
          top: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          max-width: none !important;
          transform: none !important;
        }
      }
    `;
  }

  function syncInboxChatOpenUI() {
    if (!document.querySelector(".pdp-loco")) return false;

    const isChatOpen = isShopifyInboxChatOpen();
    document.body.classList.toggle("pdp-inbox-chat-open", isChatOpen);
    applyChatOpenStackOverride(isChatOpen);
    return isChatOpen;
  }

  function applyChatBottomOffset(bottomPx) {
    const bottomValue = `${bottomPx}px`;

    getShopifyChatNodes().forEach((node) => {
      node.style.setProperty("bottom", bottomValue, "important");
      node.style.setProperty("top", "auto", "important");
      node.style.setProperty("transform", "none", "important");
    });
  }

  function getStickyBottomOffset() {
    return window.matchMedia(MOBILE_QUERY).matches ? 0 : 24;
  }

  function clearChatStackStyles() {
    document.getElementById("pdp-loco-chat-stack")?.remove();
    getShopifyChatNodes().forEach((node) => {
      node.style.removeProperty("bottom");
      node.style.removeProperty("top");
      node.style.removeProperty("transform");
    });
  }

  function updateChatStackStylesheet(chatBottom) {
    let styleEl = document.getElementById("pdp-loco-chat-stack");

    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = "pdp-loco-chat-stack";
      document.head.appendChild(styleEl);
    }

    styleEl.textContent = `
      body.pdp-sticky-cart-active:not(.pdp-inbox-chat-open) #shopify-chat,
      body.pdp-sticky-cart-active:not(.pdp-inbox-chat-open) shopify-chat,
      body.pdp-sticky-cart-active:not(.pdp-inbox-chat-open) #shopify-chat shopify-chat,
      body.pdp-sticky-cart-active:not(.pdp-inbox-chat-open) inbox-online-store-chat {
        bottom: ${chatBottom}px !important;
        top: auto !important;
        transform: none !important;
      }
    `;
  }

  function resetStickyCartChatStack(sticky) {
    document.body.classList.remove("pdp-sticky-cart-active");
    document.documentElement.style.removeProperty("--pdp-sticky-cart-bottom");
    document.documentElement.style.removeProperty("--pdp-sticky-cart-height");
    document.documentElement.style.removeProperty("--pdp-chat-bottom");
    document.documentElement.style.removeProperty("--pdp-chat-reserve-width");
    sticky?.style.removeProperty("bottom");
    clearChatStackStyles();
  }

  function syncStickyCartWithChat() {
    if (!document.querySelector(".pdp-loco")) return;

    const isChatOpen = syncInboxChatOpenUI();

    const sticky = document.querySelector(".pdp-loco .product-add-to-cart-sticky");
    const isActive = Boolean(sticky && sticky.classList.contains("sticky--visible"));
    const hasChat = hasShopifyChat();
    const isMobile = window.matchMedia(MOBILE_QUERY).matches;

    if (!isActive || !sticky) {
      resetStickyCartChatStack(sticky);

      if (hasChat && !isChatOpen) {
        getShopifyChatNodes().forEach((node) => {
          node.style.removeProperty("bottom");
          node.style.removeProperty("top");
          node.style.removeProperty("transform");
        });
      }

      return;
    }

    document.body.classList.add("pdp-sticky-cart-active");
    const gap = 16;
    const stickyBottom = getStickyBottomOffset();
    const stickyHeight = Math.ceil(sticky.getBoundingClientRect().height);
    const chatBottom = stickyBottom + stickyHeight + gap;

    document.documentElement.style.setProperty("--pdp-sticky-cart-height", `${stickyHeight}px`);
    document.documentElement.style.setProperty("--pdp-chat-bottom", `${chatBottom}px`);

    if (isMobile) {
      document.documentElement.style.removeProperty("--pdp-sticky-cart-bottom");
      sticky.style.removeProperty("bottom");
    } else {
      document.documentElement.style.setProperty("--pdp-sticky-cart-bottom", `${stickyBottom}px`);
      sticky.style.setProperty("bottom", `${stickyBottom}px`, "important");
    }

    if (hasChat) {
      if (isChatOpen) {
        document.getElementById("pdp-loco-chat-stack")?.remove();
        getShopifyChatNodes().forEach((node) => {
          node.style.removeProperty("bottom");
          node.style.removeProperty("top");
          node.style.removeProperty("transform");
        });
      } else {
        updateChatStackStylesheet(chatBottom);
        applyChatBottomOffset(chatBottom);
      }
    } else {
      document.documentElement.style.removeProperty("--pdp-chat-bottom");
      clearChatStackStyles();
    }
  }

  function whenShopifyChatReady(callback) {
    if (hasShopifyChat()) {
      callback();
      return;
    }

    const observer = new MutationObserver(() => {
      if (hasShopifyChat()) {
        observer.disconnect();
        callback();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function bindStickyCartChatStack() {
    if (document.body.dataset.pdpChatStackBound === "true") return;
    document.body.dataset.pdpChatStackBound = "true";

    const run = debounce(syncStickyCartWithChat, 50);

    whenShopifyChatReady(run);

    const sticky = document.querySelector(".pdp-loco .product-add-to-cart-sticky");
    if (sticky) {
      new MutationObserver(run).observe(sticky, {
        attributes: true,
        attributeFilter: ["class", "style"],
      });

      if (typeof ResizeObserver !== "undefined") {
        new ResizeObserver(run).observe(sticky);
      }
    }

    whenShopifyChatReady(() => {
      getShopifyChatNodes().forEach((node) => {
        if (typeof ResizeObserver !== "undefined") {
          new ResizeObserver(run).observe(node);
        }

        new MutationObserver(run).observe(node, {
          attributes: true,
          attributeFilter: ["style", "class", "is-open", "isopen", "isOpen"],
        });
      });

      document
        .querySelectorAll("#shopify-chat iframe, inbox-online-store-chat iframe")
        .forEach((iframe) => {
          new MutationObserver(run).observe(iframe, {
            attributes: true,
            attributeFilter: ["style", "isopen", "isOpen"],
          });

          if (typeof ResizeObserver !== "undefined") {
            new ResizeObserver(run).observe(iframe);
          }
        });

      run();
    });

    const chatRootObserver = new MutationObserver(run);
    chatRootObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    window.addEventListener("scroll", run, { passive: true });
    window.addEventListener("resize", debounce(run, 150));
    run();
  }

  function bindInboxChatOpenWatcher() {
    if (document.body.dataset.pdpInboxChatOpenWatcher === "true") return;
    document.body.dataset.pdpInboxChatOpenWatcher = "true";

    const run = debounce(() => {
      syncInboxChatOpenUI();
      syncStickyCartWithChat();
    }, 50);

    whenShopifyChatReady(() => {
      run();

      window.setInterval(() => {
        if (!document.querySelector(".pdp-loco")) return;

        const wasOpen = document.body.classList.contains("pdp-inbox-chat-open");
        syncInboxChatOpenUI();

        if (wasOpen !== document.body.classList.contains("pdp-inbox-chat-open")) {
          syncStickyCartWithChat();
        }
      }, 300);
    });

    document.addEventListener(
      "click",
      (event) => {
        if (!document.querySelector(".pdp-loco")) return;
        if (!event.target.closest("#shopify-chat, inbox-online-store-chat, shopify-chat, #ShopifyChat")) return;

        run();
        window.setTimeout(run, 120);
        window.setTimeout(run, 400);
        window.setTimeout(run, 900);
      },
      true
    );

    window.addEventListener("focusin", run);
  }

  function getVariantStrings() {
    return window.theme?.variantStrings || {};
  }

  function getSectionAddToCartButtons(sectionId) {
    const buttons = new Set();

    document
      .querySelectorAll(`#product-form-${sectionId} .single-add-to-cart-button`)
      .forEach((button) => buttons.add(button));

    document
      .querySelectorAll(
        `.pdp-loco product-add-to-cart-sticky[data-section="${sectionId}"] .single-add-to-cart-button`
      )
      .forEach((button) => buttons.add(button));

    return [...buttons];
  }

  const pdpAtcPendingSections = new Set();
  const pdpAtcReleaseTimers = new Map();

  function setSectionAddToCartLoading(sectionId, isLoading) {
    getSectionAddToCartButtons(sectionId).forEach((button) => {
      if (isLoading) {
        button.dataset.atcSubmitting = "true";
        button.setAttribute("aria-busy", "true");
        return;
      }

      delete button.dataset.atcSubmitting;
      button.removeAttribute("aria-busy");
    });
  }

  function releasePdpAddToCartSubmit(sectionId) {
    if (!sectionId || !pdpAtcPendingSections.has(sectionId)) return;

    pdpAtcPendingSections.delete(sectionId);

    const timer = pdpAtcReleaseTimers.get(sectionId);
    if (timer) {
      clearTimeout(timer);
      pdpAtcReleaseTimers.delete(sectionId);
    }

    setSectionAddToCartLoading(sectionId, false);
    refreshStickyAddToCartState(sectionId);
  }

  function armPdpAddToCartRelease(sectionId) {
    const existingTimer = pdpAtcReleaseTimers.get(sectionId);
    if (existingTimer) clearTimeout(existingTimer);

    pdpAtcReleaseTimers.set(
      sectionId,
      window.setTimeout(() => releasePdpAddToCartSubmit(sectionId), 2500)
    );
  }

  function beginPdpAddToCartSubmit(sectionId) {
    if (!sectionId || pdpAtcPendingSections.has(sectionId)) return false;

    pdpAtcPendingSections.add(sectionId);
    setSectionAddToCartLoading(sectionId, true);
    armPdpAddToCartRelease(sectionId);
    return true;
  }

  function ensurePdpAddToCartReleaseListeners() {
    if (document.documentElement.dataset.pdpAtcReleaseBound === "true") return;
    document.documentElement.dataset.pdpAtcReleaseBound = "true";

    const releasePendingSections = () => {
      [...pdpAtcPendingSections].forEach((sectionId) => releasePdpAddToCartSubmit(sectionId));
    };

    document.addEventListener("cart:item-added", releasePendingSections);
    window.MinimogEvents?.subscribe?.("ON_ITEM_ADDED", releasePendingSections);

    if (!document.documentElement.dataset.pdpAtcAddingObserverBound) {
      document.documentElement.dataset.pdpAtcAddingObserverBound = "true";

      const addingObserver = new MutationObserver((mutations) => {
        const shouldRelease = mutations.some((mutation) => {
          if (mutation.type !== "attributes" || mutation.attributeName !== "class") return false;
          const target = mutation.target;
          return target instanceof Element && target.classList.contains("product-form") && !target.classList.contains("adding");
        });

        if (shouldRelease) {
          releasePendingSections();
        }
      });

      document.querySelectorAll(".pdp-loco .product-form").forEach((formWrap) => {
        addingObserver.observe(formWrap, { attributes: true, attributeFilter: ["class"] });
      });

      document.addEventListener("shopify:section:load", (event) => {
        event.target?.querySelectorAll?.(".pdp-loco .product-form")?.forEach((formWrap) => {
          addingObserver.observe(formWrap, { attributes: true, attributeFilter: ["class"] });
        });
      });
    }

    if (window.__pdpAtcFetchHooked) return;
    window.__pdpAtcFetchHooked = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = (...args) => {
      const request = args[0];
      const url = typeof request === "string" ? request : request?.url || "";
      const isCartAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);
      const result = originalFetch(...args);

      if (isCartAdd && pdpAtcPendingSections.size > 0) {
        Promise.resolve(result)
          .catch(() => {})
          .finally(releasePendingSections);
      }

      return result;
    };
  }

  function bindPdpAddToCartSubmitGuard(scope) {
    ensurePdpAddToCartReleaseListeners();

    scope.querySelectorAll(".pdp-loco").forEach((sectionRoot) => {
      const sectionId =
        sectionRoot.querySelector(`product-form[data-section]`)?.dataset?.section ||
        sectionRoot.querySelector("product-add-to-cart-sticky[data-section]")?.dataset?.section;

      if (!sectionId) return;

      const mainForm = document.getElementById(`product-form-${sectionId}`);
      if (mainForm && mainForm.dataset.pdpAtcSubmitBound !== "true") {
        mainForm.dataset.pdpAtcSubmitBound = "true";
        mainForm.addEventListener(
          "submit",
          (event) => {
            if (pdpAtcPendingSections.has(sectionId)) {
              event.preventDefault();
              event.stopImmediatePropagation();
              return;
            }

            beginPdpAddToCartSubmit(sectionId);
          },
          true
        );
      }
    });
  }

  function updateSectionAddToCartButtons(sectionId, variant) {
    const strings = getVariantStrings();
    const stickyForm = document.querySelector(
      `product-form.product-add-to-cart-sticky--form[data-section="${sectionId}"]`
    );
    const mainForm = document.querySelector(`product-form[data-section="${sectionId}"]:not([data-sticky])`);
    const template = stickyForm?.dataset.template || mainForm?.dataset.template || "";

    getSectionAddToCartButtons(sectionId).forEach((button) => {
      const textEl = button.querySelector(".single-add-to-cart-button--text");
      if (!textEl) return;

      if (!variant) {
        textEl.textContent = strings.unavailable || textEl.textContent;
        button.disabled = true;
        button.classList.add("sold-out");
        button.setAttribute("aria-disabled", "true");
        return;
      }

      if (!variant.available) {
        textEl.textContent = strings.soldOut || textEl.textContent;
        button.disabled = true;
        button.classList.add("sold-out");
        button.setAttribute("aria-disabled", "true");
        return;
      }

      button.disabled = false;
      button.classList.remove("sold-out");
      button.removeAttribute("aria-disabled");
      textEl.textContent = template.includes("pre-order")
        ? strings.preOrder || textEl.textContent
        : strings.addToCart || textEl.textContent;
    });
  }

  function syncStickySelectToMain(select, sectionId) {
    const mainVariantSelects = document.getElementById(`variant-selects-${sectionId}`);
    if (!mainVariantSelects || !select) return false;

    const fieldset = select.closest("fieldset[data-handle]");
    if (!fieldset) return false;
    if (fieldset.classList.contains("product-form__input--sticky-color-meta")) return false;

    const handle = fieldset.dataset.handle;
    const value = select.value;
    const mainFieldset = mainVariantSelects.querySelector(`fieldset[data-handle="${handle}"]`);
    if (!mainFieldset) return false;

    const mainSelect = mainFieldset.querySelector("select");
    if (mainSelect) {
      if (mainSelect.value === value) return true;
      mainSelect.value = value;
      mainSelect.dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    }

    const escapedValue =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(value)
        : value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    const mainRadio = mainFieldset.querySelector(`input[type="radio"][value="${escapedValue}"]`);
    if (mainRadio) {
      if (mainRadio.checked) return true;
      mainRadio.checked = true;
      mainRadio.dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    }

    return false;
  }

  function resolveMainVariant(sectionId) {
    const mainVariantSelects = document.getElementById(`variant-selects-${sectionId}`);
    return mainVariantSelects?.currentVariant || null;
  }

  function refreshStickyAddToCartState(sectionId) {
    updateSectionAddToCartButtons(sectionId, resolveMainVariant(sectionId));
  }

  function normalizeImageUrl(url) {
    if (!url) return "";

    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return String(url).split("?")[0].split("#")[0];
    }
  }

  function extractImageSrcFromHtml(html) {
    if (!html) return "";

    const srcMatch = String(html).match(/\bsrc\s*=\s*(["'])(.*?)\1/i);
    if (srcMatch?.[2]) return srcMatch[2];

    const srcsetMatch = String(html).match(/\bsrcset\s*=\s*(["'])(.*?)\1/i);
    if (srcsetMatch?.[2]) {
      return srcsetMatch[2].split(",")[0]?.trim().split(/\s+/)[0] || "";
    }

    return "";
  }

  function getStickyImageSrc(container) {
    const img = container?.querySelector("img");
    if (!img) return "";

    return img.currentSrc || img.src || img.getAttribute("src") || "";
  }

  function protectStickyProductImage(sectionId) {
    const container = document.getElementById(`product-image-${sectionId}--sticky`);
    if (!container || container.dataset.pdpStickyImageGuard === "true") return;
    container.dataset.pdpStickyImageGuard = "true";

    const htmlDescriptor = Object.getOwnPropertyDescriptor(Element.prototype, "innerHTML");
    if (!htmlDescriptor?.set || !htmlDescriptor?.get) return;

    Object.defineProperty(container, "innerHTML", {
      configurable: true,
      get() {
        return htmlDescriptor.get.call(this);
      },
      set(nextHtml) {
        const currentSrc = getStickyImageSrc(this);
        const nextSrc = extractImageSrcFromHtml(nextHtml);

        if (
          currentSrc &&
          nextSrc &&
          normalizeImageUrl(currentSrc) === normalizeImageUrl(nextSrc)
        ) {
          return;
        }

        htmlDescriptor.set.call(this, nextHtml);
      },
    });
  }

  function initStickyVariantSync(sticky) {
    const sectionId = sticky.dataset.section;
    const stickyVariantSelects = sticky.querySelector("variant-selects");
    if (!sectionId || !stickyVariantSelects) return;
    if (stickyVariantSelects.dataset.pdpStickyVariantSyncBound === "true") return;
    stickyVariantSelects.dataset.pdpStickyVariantSyncBound = "true";

    stickyVariantSelects.addEventListener(
      "change",
      (event) => {
        const select = event.target.closest("select");
        if (!select || !sticky.contains(select)) return;

        syncStickySelectToMain(select, sectionId);

        window.requestAnimationFrame(() => {
          refreshStickyAddToCartState(sectionId);
          window.LocoDropdown?.syncFromNative?.(sticky);
        });
      },
      true
    );
  }

  function initStickyAddToCart(scope) {
    scope.querySelectorAll(".pdp-loco .product-add-to-cart-sticky").forEach((sticky) => {
      const content = sticky.querySelector(".product-add-to-cart-sticky--content");
      if (content) {
        content.classList.add("active");
        content.style.removeProperty("height");
        content.style.removeProperty("max-height");
        content.style.removeProperty("overflow");
      }

      const inner = sticky.querySelector(".product-add-to-cart-sticky--inner");
      if (inner && inner.tagName === "BUTTON") {
        const header = document.createElement("div");
        header.className = inner.className;
        header.innerHTML = inner.innerHTML;
        inner.replaceWith(header);
      }

      window.LocoDropdown?.init(sticky);
      window.LocoDropdown?.syncFromNative?.(sticky);
      initStickyTitleTooltips(sticky);
      initStickyVariantSync(sticky);
      protectStickyProductImage(sticky.dataset.section);
      refreshStickyAddToCartState(sticky.dataset.section);
    });
  }

  function initStickyTitleTooltips(sticky) {
    sticky.querySelectorAll(".product-add-to-cart-sticky--title").forEach((titleEl) => {
      if (titleEl.dataset.stickyTitleTooltipBound === "true") return;
      titleEl.dataset.stickyTitleTooltipBound = "true";

      const fullTitle = (titleEl.dataset.fullTitle || titleEl.textContent || "").trim();
      if (!fullTitle) return;
      titleEl.dataset.fullTitle = fullTitle;

      let tooltip = null;

      const isTruncated = () => titleEl.scrollWidth > titleEl.clientWidth + 1;

      const syncTruncated = () => {
        titleEl.classList.toggle("is-truncated", isTruncated());
      };

      const removeTooltip = () => {
        tooltip?.remove();
        tooltip = null;
      };

      const positionTooltip = () => {
        if (!tooltip) return;

        const rect = titleEl.getBoundingClientRect();
        const gap = 8;

        tooltip.style.left = `${Math.max(8, rect.left)}px`;
        tooltip.style.top = `${rect.top - tooltip.offsetHeight - gap}px`;

        if (rect.top - tooltip.offsetHeight - gap < 8) {
          tooltip.style.top = `${rect.bottom + gap}px`;
        }

        const tipRect = tooltip.getBoundingClientRect();
        if (tipRect.right > window.innerWidth - 8) {
          tooltip.style.left = `${Math.max(8, window.innerWidth - tipRect.width - 8)}px`;
        }
      };

      const showTooltip = () => {
        if (!isTruncated()) return;

        removeTooltip();
        tooltip = document.createElement("div");
        tooltip.className = "product-add-to-cart-sticky--title-tooltip";
        tooltip.setAttribute("role", "tooltip");
        tooltip.textContent = fullTitle;
        document.body.appendChild(tooltip);
        positionTooltip();
      };

      syncTruncated();
      titleEl.addEventListener("mouseenter", showTooltip);
      titleEl.addEventListener("mouseleave", removeTooltip);
      window.addEventListener(
        "scroll",
        () => {
          if (tooltip) positionTooltip();
        },
        { passive: true }
      );
      window.addEventListener("resize", debounce(syncTruncated, 150));
    });
  }

  function initFeatureBadges(root) {
    const FLAG_EMOJI_REGEX = /(?:\uD83C[\uDDE6-\uDDFF]){2}/g;
    const TRAILING_CODES_REGEX = /\s+([A-Za-z]{2}(?:\s+[A-Za-z]{2})*)\s*$/i;

    function parseBadgeLabel(text) {
      let label = text.trim();
      const codes = [];

      label = label.replace(FLAG_EMOJI_REGEX, " ").replace(/\s{2,}/g, " ").trim();

      const trailingMatch = label.match(TRAILING_CODES_REGEX);
      if (trailingMatch) {
        (trailingMatch[1].match(/[A-Za-z]{2}/gi) || []).forEach((code) => {
          codes.push(code.toUpperCase());
        });
        label = label.slice(0, trailingMatch.index).trim();
      }

      return {
        label,
        codes: [...new Set(codes)],
      };
    }

    const TWEMOJI_FLAG_CDN = "https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg";

    function countryCodeToTwemojiSlug(code) {
      const normalized = (code.trim().toUpperCase() === "UK" ? "GB" : code.trim().toUpperCase());
      if (!/^[A-Z]{2}$/.test(normalized)) return "";

      const base = 0x1f1e6;
      const cp1 = (base + normalized.charCodeAt(0) - 65).toString(16);
      const cp2 = (base + normalized.charCodeAt(1) - 65).toString(16);
      return `${cp1}-${cp2}`;
    }

    function createFlagTwemoji(code) {
      const slug = countryCodeToTwemojiSlug(code);
      if (!slug) return null;

      const img = document.createElement("img");
      img.className = "pdp-loco__badge-flag pdp-loco__badge-flag--twemoji";
      img.src = `${TWEMOJI_FLAG_CDN}/${slug}.svg`;
      img.width = 18;
      img.height = 18;
      img.alt = "";
      img.loading = "lazy";
      img.decoding = "async";
      img.setAttribute("aria-hidden", "true");
      return img;
    }

    function enhanceBadgeText(el) {
      if (el.dataset.pdpBadgeEnhanced === "true" || el.querySelector(".pdp-loco__badge-label")) return;

      const original = el.textContent.trim();
      if (!original) return;

      const { label, codes } = parseBadgeLabel(original);
      el.dataset.pdpBadgeEnhanced = "true";
      el.setAttribute("aria-label", original);
      el.replaceChildren();

      const labelNode = document.createElement("span");
      labelNode.className = "pdp-loco__badge-label";
      labelNode.textContent = label;
      el.appendChild(labelNode);

      if (!codes.length) return;

      el.classList.add("pdp-loco__badge-text--has-flags");

      const flagsWrap = document.createElement("span");
      flagsWrap.className = "pdp-loco__badge-flags";

      codes.forEach((code) => {
        const flagNode = createFlagTwemoji(code);
        if (flagNode) flagsWrap.appendChild(flagNode);
      });

      if (flagsWrap.childElementCount) {
        el.appendChild(flagsWrap);
      }
    }

    (root || document).querySelectorAll(".pdp-loco__badge-text").forEach(enhanceBadgeText);
  }

  function initProductAccordions(root) {
    (root || document).querySelectorAll(".pdp-loco .pdp-loco__info").forEach((info) => {
      if (info.dataset.pdpAccordionBound === "true") return;
      info.dataset.pdpAccordionBound = "true";

      const accordions = info.querySelectorAll(".product__accordion details");
      const initiallyOpen = [...accordions].filter((item) => item.open);

      if (initiallyOpen.length > 1) {
        initiallyOpen.slice(1).forEach((item) => {
          item.open = false;
        });
      }

      accordions.forEach((details) => {
        details.addEventListener("toggle", () => {
          if (!details.open) return;

          accordions.forEach((other) => {
            if (other !== details && other.open) {
              other.open = false;
            }
          });
        });
      });
    });
  }

  function init(root) {
    const scope = root || document;
    ensureCompleteLookDocumentClose();
    ensureLightboxVideoResizeListener();
    bindPdpAddToCartSubmitGuard(scope);
    scope.querySelectorAll(".pdp-loco").forEach((section) => {
      initFeatureBadges(section);
      initCompleteLook(section);
      initSatisfiedCustomers(section);
      initGoogleReviews(section);
      bindSectionHeadings(section);
      initMobileGallery(section);
      initGalleryVideos(section);
      initStickyAddToCart(section);
      initProductAccordions(section);
    });

    if (scope === document || scope.querySelector?.(".pdp-loco")) {
      bindStickyCartChatStack();
      bindInboxChatOpenWatcher();
      syncStickyCartWithChat();
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  window.addEventListener(
    "resize",
    debounce(() => {
      document.querySelectorAll(".pdp-loco .product-image-container").forEach((container) => {
        delete container.dataset.pdpGalleryBound;
      });
      document.querySelectorAll(".pdp-loco .product-images").forEach((slider) => {
        delete slider.dataset.pdpGalleryVideosBound;
      });
      init();
    }, 200)
  );

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  document.addEventListener("product:variant-change", (event) => {
    const sectionId = event.detail?.sectionId;
    if (sectionId) {
      refreshStickyAddToCartState(sectionId);
    }

    document.querySelectorAll(".pdp-loco .product-add-to-cart-sticky").forEach((sticky) => {
      window.LocoDropdown?.syncFromNative?.(sticky);
    });
  });
})();
